/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Send, User, Bot, Star, Home, Settings, LogOut, BarChart3, RotateCcw, X, Menu } from 'lucide-react';
import LandingPage from './LandingPage';
import HomePage from './HomePage';
import AdminDashboard from './AdminDashboard';
import SettingsPage from './SettingsPage';
import UserDashboard from './UserDashboard';
import TeacherClassrooms from './TeacherClassrooms';
import ClassroomDetail from './ClassroomDetail';
import SharedVisual from './SharedVisual';
import { authService } from './services/AuthService.js';
import { supabaseAuthService } from './services/SupabaseAuthService.js';
import { supabase } from './lib/supabase.js';
import { debugLog } from './utils/debugLog';
import ResetPasswordPage from './ResetPasswordPage';
import AboutPage from './AboutPage';
import SessionSummaryReport from './SessionSummaryReport';

// Jamie's Animated Face Component
// Helper function to safely calculate minimum DQ score, filtering out NaN and invalid values
const safeMinDqScore = (dqScoreObj) => {
  if (!dqScoreObj || typeof dqScoreObj !== 'object') return 0;
  const values = Object.values(dqScoreObj)
    .filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v) && v >= 0 && v <= 1);
  return values.length > 0 ? Math.min(...values) : 0;
};

const MAX_INPUT_HEIGHT = 120;

const JamieFace = ({ dqScore, avgDqScore, size = 'small' }) => {
  const getJamieState = (score) => {
    if (score >= 0.8) return 'confident';
    if (score >= 0.6) return 'thoughtful';
    if (score >= 0.3) return 'uncertain';
    return 'confused';
  };

  const state = getJamieState(avgDqScore || 0);
  const isLarge = size === 'large';
  const faceSize = isLarge ? 'w-12 h-12' : 'w-10 h-10';

  const faceStyles = {
    confused: {
      emoji: '😵‍💫',
      gradient: 'from-red-100 to-red-200',
      animation: 'animate-pulse'
    },
    uncertain: {
      emoji: '😟',
      gradient: 'from-yellow-100 to-yellow-200',
      animation: 'animate-bounce'
    },
    thoughtful: {
      emoji: '🤔',
      gradient: 'from-blue-100 to-blue-200',
      animation: 'animate-pulse'
    },
    confident: {
      emoji: '😊',
      gradient: 'from-green-100 to-green-200',
      animation: 'animate-pulse'
    }
  };

  const currentStyle = faceStyles[state];

  return (
    <div className={`${faceSize} rounded-full bg-gradient-to-br ${currentStyle.gradient} flex items-center justify-center shadow-md ${currentStyle.animation} transition-all duration-500`}>
      <div className={isLarge ? 'text-2xl' : 'text-lg'}>
        {currentStyle.emoji}
      </div>
    </div>
  );
};

const ALLOWED_VIEWS = [
  'homepage',
  'chat',
  'dashboard',
  'admin',
  'settings',
  'classrooms',
  'classroom-detail'
];

const getSavedCharacter = () => {
  if (typeof window === 'undefined') return 'jamie';
  const saved = localStorage.getItem('currentCharacter') || 'jamie';
  return saved === 'daniel' ? 'alex' : saved;
};

const MainApp = () => {
  // Feature flag to use Supabase authentication
  const USE_SUPABASE_AUTH = true; // Re-enable Supabase authentication

  // Check for existing Supabase session synchronously before first render
  const getInitialAuthState = () => {
    if (USE_SUPABASE_AUTH) {
      // Check localStorage for Supabase session (Supabase stores it with keys starting with 'sb-')
      try {
        // Look for any Supabase auth token in localStorage
        let hasSession = false;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('sb-') && key.includes('auth-token')) {
            hasSession = true;
            break;
          }
        }
        
        if (hasSession) {
          // Session exists, try to get user from auth service
          const currentUser = supabaseAuthService.getCurrentUser();
          if (currentUser) {
            debugLog('User already logged in on mount (from session):', currentUser);
            return currentUser;
          }
          // If session token exists but getCurrentUser() returns null,
          // the service might not be initialized yet - return null and let useEffect handle it
          // isAuthChecking will remain true, preventing any render
        }
      } catch (e) {
        debugLog('Error checking session storage:', e);
      }
    }
    return null;
  };

  // User information state - now using auth service
  const [userInfo, setUserInfo] = useState(() => {
    return getInitialAuthState();
  });

  // Add loading state to prevent flash of landing page
  // Always check auth on mount if using Supabase to prevent flash
  // Also check if there's a session token - if so, keep checking until confirmed
  const [isAuthChecking, setIsAuthChecking] = useState(() => {
    if (!USE_SUPABASE_AUTH) return false;
    // Check if there's a session token - if so, we need to verify it
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.includes('auth-token')) {
          // Session token exists, keep checking until confirmed
          return true;
        }
      }
    } catch (e) {
      // Ignore errors
    }
    // No session token, but still check auth if using Supabase
    return true;
  });
  
  // Initialize authentication on component mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isInitialized = false;
    
    const initializeAuth = async () => {
      if (isInitialized) return;
      isInitialized = true;
      
      if (USE_SUPABASE_AUTH) {
        // Initialize Supabase auth service
        await supabaseAuthService.init();
        
        // Clear any existing listeners first (for hot reloading)
        supabaseAuthService.clearAllListeners();
        
        // Set up auth state listener
        supabaseAuthService.addListener((event, user) => {
            debugLog('Auth state changed:', event, user);
            debugLog('Current view when auth state changes:', currentView);
            debugLog('Current isLoadingProgress state:', isLoadingProgress);
            debugLog('Current hasStartedLoading state:', hasStartedLoading);
            
            if (event === 'progress_loaded' || event === 'progress_updated') {
              // Update userInfo when progress is loaded/updated
              debugLog('Updating userInfo due to progress event:', event);
              debugLog('Setting isLoadingProgress to false');
              debugLog('User progress data:', user?.progress);
              debugLog('Full user object:', JSON.stringify(user, null, 2));
              // Clear any existing timeout
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
              }
              setUserInfo(user);
              setIsLoadingProgress(false);
              setHasStartedLoading(false);
            } else if (event === 'progress_loading_started') {
              // Progress loading has started
              debugLog('Progress loading started, setting loading state');
              setIsLoadingProgress(true);
            } else if (event === 'progress_loading_failed') {
              // Progress loading failed
              debugLog('Progress loading failed, resetting loading state');
              // Clear any existing timeout
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
              }
              setIsLoadingProgress(false);
              setHasStartedLoading(false);
            } else if (event === 'login' && user) {
              // When user logs in, preserve existing view or restore from sessionStorage
              debugLog('User logged in via auth listener');
              debugLog('User role:', user.role);
              setUserInfo(user);
              // Preserve current view, or restore from sessionStorage, or default to homepage
              setCurrentView(prev => {
                if (prev) return prev; // Keep existing view
                // Try to restore from sessionStorage
                const savedView = sessionStorage.getItem('currentView');
                if (savedView && ALLOWED_VIEWS.includes(savedView)) {
                  debugLog('Restoring view from sessionStorage on login:', savedView);
                  return savedView;
                }
                return 'homepage'; // Last resort default
              });
              // Don't set loading state here - let the useEffect handle it
              debugLog('Login event processed, useEffect will handle progress loading');
            } else if (event === 'logout') {
              // When user logs out, clear all state
              debugLog('User logged out via auth listener, clearing state');
              // Clear any existing timeout
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
              }
              setUserInfo(null);
              setCurrentView(null);
              setMessages([]);
              setIsLoadingProgress(false);
              setHasStartedLoading(false);
              setLoadedUserIds(new Set()); // Clear loaded user IDs
              // Clear sessionStorage
              sessionStorage.removeItem('currentView');
            } else if (event === 'classroom_joined' || event === 'classroom_created') {
              // Don't update userInfo for classroom events - these pass classroom objects, not user objects
              debugLog('Classroom event received, not updating userInfo:', event);
              // The userInfo should remain as the User object, not be replaced with classroom
            } else if (user && typeof user === 'object' && user.role) {
              // Only update userInfo if user is actually a User object (has a role property)
              debugLog('Updating userInfo due to auth event:', event);
              setUserInfo(user);
            } else {
              debugLog('Ignoring auth event that doesn\'t contain valid user data:', event, user);
            }
          });
        
        // Make authService available in console for demo
        window.authService = supabaseAuthService;
        
        // Add debugging helper
        window.debugAuth = () => {
          debugLog('Current user:', supabaseAuthService.getCurrentUser());
          debugLog('Is loading progress:', supabaseAuthService.isLoadingProgress);
          debugLog('Listeners count:', supabaseAuthService.listeners.length);
        };

        // Hydrate existing session on refresh
        const existingUser = supabaseAuthService.getCurrentUser();
        if (existingUser) {
          debugLog('Hydrating user from existing Supabase session:', existingUser);
          console.log('🔍 DEBUG: Existing user progress:', existingUser.progress);
          console.log('🔍 DEBUG: Jamie progress:', existingUser.progress?.jamie);
          console.log('🔍 DEBUG: Jamie sessions:', existingUser.progress?.jamie?.sessions);
          console.log('🔍 DEBUG: Jamie lastSession:', existingUser.progress?.jamie?.lastSession);
          console.log('🔍 DEBUG: Jamie completed:', existingUser.progress?.jamie?.completed);
          // Restore view from sessionStorage FIRST
          const savedView = sessionStorage.getItem('currentView');
          const viewToSet = (savedView && ALLOWED_VIEWS.includes(savedView)) ? savedView : 'homepage';
          debugLog('Restoring view from sessionStorage during auth init:', viewToSet);
          
          // Set both userInfo and currentView in the same batch
          // Then clear isAuthChecking - React will batch these updates
          setUserInfo(existingUser);
          setCurrentView(viewToSet);
          console.log('🔍 DEBUG: Set userInfo with progress:', existingUser.progress);
        } else {
          // No user found, ensure we're on landing page
          setCurrentView(null);
        }
        
        // Clear auth checking state after initialization
        // React batches synchronous state updates, so currentView will be set
        // before isAuthChecking becomes false in the same render cycle
        setIsAuthChecking(false);
      } else {
        // Use original auth service
        authService.init();
        setUserInfo(authService.getCurrentUser());
        // Make authService available in console for demo
        window.authService = authService;
        setIsAuthChecking(false);
      }
    };

    initializeAuth();
  }, []); // Empty dependency array - run once on mount

  // Loading state for progress
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const loadingTimeoutRef = useRef(null); // Use ref instead of state for timeout ID
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const [loadedUserIds, setLoadedUserIds] = useState(new Set()); // Track which users we've loaded progress for

  // Ensure progress is loaded for students who are already logged in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (USE_SUPABASE_AUTH && userInfo && (userInfo.role === 'student' || userInfo.role === 'teacher')) {
      // Check if we've already loaded progress for this user
      const hasLoadedForUser = loadedUserIds.has(userInfo.id);
      
      // Only start loading if we haven't already started, not currently loading, and haven't loaded for this user
      if (!hasStartedLoading && !isLoadingProgress && !hasLoadedForUser) {
        setHasStartedLoading(true);
        setIsLoadingProgress(true);
        
        // Mark this user as having progress loaded
        setLoadedUserIds(prev => new Set(prev).add(userInfo.id));
        
        // Set a timeout to prevent infinite loading
        // Clear any existing timeout first
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        loadingTimeoutRef.current = setTimeout(() => {
          setIsLoadingProgress(false);
          setHasStartedLoading(false);
          loadingTimeoutRef.current = null;
        }, 10000); // 10 second timeout
        
        supabaseAuthService.loadProgressFromSupabase();
      }
    } else if (userInfo && userInfo.role !== 'student' && userInfo.role !== 'teacher') {
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      // Ensure loading state is false for non-students
      if (isLoadingProgress) {
        setIsLoadingProgress(false);
      }
      setHasStartedLoading(false);
    } else if (!userInfo) {
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setIsLoadingProgress(false);
      setHasStartedLoading(false);
    }
  }, [userInfo?.id, userInfo?.role]); // Only depend on user ID and role, not loading states

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, []);
  const [gameMode, setGameMode] = useState(() => {
    // Load game mode from localStorage on component mount
    return localStorage.getItem('gameMode') || 'game';
  });
  
  // Initialize currentView to null - will be set after auth check to prevent flash
  const [currentView, setCurrentView] = useState(null); // 'homepage', 'chat', 'dashboard', 'admin', 'settings', 'classrooms', 'classroom-detail'
  
  // Save currentView to sessionStorage whenever it changes (if user is authenticated)
  useEffect(() => {
    if (userInfo && currentView) {
      sessionStorage.setItem('currentView', currentView);
      debugLog('Saved currentView to sessionStorage:', currentView);
    }
  }, [currentView, userInfo]);

  const [currentCharacter, setCurrentCharacter] = useState(() => getSavedCharacter()); // 'jamie', 'andres', or 'kavya'

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('currentCharacter', currentCharacter);
    }
  }, [currentCharacter]);
  const [selectedClassroomId, setSelectedClassroomId] = useState(null);
  
  // Character data
  const characterData = {
    jamie: {
      name: 'Jamie',
      title: 'Mechanical Engineering',
      context: 'Jamie is a sophomore mechanical engineering student considering switching to art/design. He\'s worried about disappointing his immigrant parents. How would you coach him?',
      progressLabel: 'Jamie\'s Progress',
      gameMode: 'assessment' // Jamie uses assessment mode
    },
    andres: {
      name: 'Andres',
      title: 'Software Engineering',
      context: 'Andres is a software engineer considering a career pivot to product management. He\'s feeling burnt out from coding and wants to work more with people and strategy. How would you coach him?',
      progressLabel: 'Andres\'s Progress',
      gameMode: 'game' // Andres uses game mode
    },
    kavya: {
      name: 'Kavya',
      title: 'Career Exploration',
      context: 'Kavya is a recent graduate exploring career options. She\'s torn between pursuing a traditional corporate path or starting her own business. She values work-life balance and wants to make a meaningful impact. How would you coach her?',
      progressLabel: 'Kavya\'s Progress',
      gameMode: 'game' // Kavya uses game mode
    },
    alex: {
      name: 'Alex',
      title: 'College Path Decision',
      context: 'Alex is a high school senior with strong grades and solid test scores. Teachers and family assume college is the obvious next step, but no career feels exciting and every application feels like committing to an unknown future. As deadlines approach, Alex is torn between applying now on the expected path or pausing to explore first. How would you coach them?',
      progressLabel: 'Alex\'s Progress',
      gameMode: 'game'
    },
    sarah: {
      name: 'Sarah',
      title: 'Returning to Work',
      context: 'Sarah is a professional returning to work after a career break. She\'s choosing between returning to her previous field where she has existing skills and experience, or starting in a new field/career direction that might be more aligned with her current life priorities. How would you coach her?',
      progressLabel: 'Sarah\'s Progress',
      gameMode: 'game' // Sarah uses game mode
    }
  };
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [thinkingText, setThinkingText] = useState('Thinking');
  const [isTextTransitioning, setIsTextTransitioning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('untested');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCharacterInfo, setShowCharacterInfo] = useState(false);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [pendingCharacterId, setPendingCharacterId] = useState(null);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [demoMode, setDemoMode] = useState(false); // Start with real backend

  // Detect mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Helper function to get max attempts based on character
  const getMaxAttempts = (character) => {
    if (character === 'kavya') return 10;
    if (character === 'alex') return 10;
    if (character === 'sarah') return 10;
    if (character === 'jamie') return 10;
    return 20; // Default for andres and others
  };
  
  const [attemptsRemaining, setAttemptsRemaining] = useState(() => {
    const savedCharacter = getSavedCharacter();
    return getMaxAttempts(savedCharacter);
  });
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isProgressAnimating, setIsProgressAnimating] = useState(false);
  const [isNewSession, setIsNewSession] = useState(true); // Track if next message should trigger reset
  // LLM-generated personalized session review (loaded at end of session)
  const [sessionReview, setSessionReview] = useState(null);
  const [sessionReviewStatus, setSessionReviewStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'failed'
  
  // Refs
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const hasRestoredSessionRef = useRef(false);
  
  // Debug: Track when attemptsRemaining changes
  useEffect(() => {
    console.log(`[Attempts State] attemptsRemaining changed to: ${attemptsRemaining}`);
  }, [attemptsRemaining]);

  const adjustMessageInputHeight = (element) => {
    if (!element) return;
    element.style.height = 'auto';
    const newHeight = Math.min(element.scrollHeight, MAX_INPUT_HEIGHT);
    element.style.height = `${newHeight}px`;
    element.style.overflowY = element.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
  };

  useEffect(() => {
    adjustMessageInputHeight(messageInputRef.current);
  }, [currentMessage]);

  // Handle login from landing page
  const handleLogin = async (loginData) => {
    try {
      debugLog('handleLogin called with:', loginData);
      
      // Check if user is already logged in
      if (userInfo && userInfo.id) {
        debugLog('User already logged in, skipping login');
        debugLog('Current view before setting currentView:', currentView);
        setCurrentView('homepage');
        debugLog('State updated - keeping existing userInfo, currentView: homepage');
        return { success: true, message: 'Already logged in' };
      }
      
      // Check if user is already authenticated at Supabase level
      // But verify that a session actually exists (not just a local user object)
      if (USE_SUPABASE_AUTH && supabaseAuthService.currentUser) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          debugLog('User already authenticated at Supabase level with valid session, hydrating state');
          debugLog('Current view before setting currentView:', currentView);
          setUserInfo(prev => prev || supabaseAuthService.currentUser);
          setCurrentView('homepage');
          debugLog('State updated - userInfo hydrated from Supabase, currentView: homepage');
          return { success: true, message: 'Already logged in' };
        } else {
          debugLog('User object exists but no Supabase session - user may need to confirm email');
          // Clear the user object since session doesn't exist
          supabaseAuthService.currentUser = null;
          // Continue with login flow
        }
      }
      
      // Set the game mode from landing page
      if (loginData.gameMode) {
        setGameMode(loginData.gameMode);
        localStorage.setItem('gameMode', loginData.gameMode);
      }
      
      // Use authentication service for login
      debugLog('Using Supabase auth:', USE_SUPABASE_AUTH);
      const result = USE_SUPABASE_AUTH 
        ? await supabaseAuthService.login({
            email: loginData.email,
            password: loginData.password || 'demo' // For demo purposes
          })
        : await authService.login({
            email: loginData.email,
            password: loginData.password || 'demo' // For demo purposes
          });
      
      debugLog('Auth service result:', result);
      
      if (result.success) {
        debugLog('Login successful, setting userInfo to:', result.user);
        debugLog('Current view before setting userInfo:', currentView);
        setUserInfo(result.user);
        debugLog('Setting currentView to homepage');
        setCurrentView('homepage');
        debugLog('State updated - userInfo:', result.user, 'currentView: homepage');
        return { success: true, message: result.message };
      } else {
        debugLog('Login failed:', result.error);
        return { success: false, message: result.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  // Handle starting a coaching session
  const handleStartCoaching = (session) => {
    setCurrentView('chat');
    
    // Try to load in-progress session
    const savedSession = loadInProgressSession(currentCharacter);
    if (savedSession && savedSession.messages.length > 0) {
      // Restore saved session
      setMessages(savedSession.messages);
      setAttemptsRemaining(savedSession.attemptsRemaining);
    } else {
      // Reset chat state for new session
      setMessages([]);
      setAttemptsRemaining(getMaxAttempts(currentCharacter));
      setIsNewSession(true); // Mark as new session when starting fresh
    }
    
    setCurrentMessage('');
    setIsLoading(false);
    setIsTyping(false);
    setAnimatedProgress(0);
    setIsProgressAnimating(false);
  };

  // Handle character selection
  const handleCharacterClick = (characterId) => {
    debugLog('Character clicked:', characterId, 'Setting currentView to chat');
    debugLog('Current isLoadingProgress:', isLoadingProgress);
    
    // Prevent clicking if progress is still loading
    if (isLoadingProgress) {
      console.log('Progress is still loading, preventing character click');
      return;
    }
    
    // Save current session before switching
    if (messages.length > 0) {
      saveInProgressSession();
    }
    
    setCurrentCharacter(characterId);
    setCurrentView('chat');
    
    // Load saved session for new character
    const savedSession = loadInProgressSession(characterId);
    if (savedSession && savedSession.messages.length > 0) {
      setMessages(savedSession.messages);
      setAttemptsRemaining(savedSession.attemptsRemaining);
    } else {
      setMessages([]);
      setAttemptsRemaining(getMaxAttempts(characterId));
    }
    
    // Set pending character to show scenario modal after chat loads
    setPendingCharacterId(characterId);
    setIsModalClosing(false);
    // Show modal after a delay to let chat view render and messages load
    // Use requestAnimationFrame twice to ensure DOM is ready and messages are set
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          setShowScenarioModal(true);
        }, 150);
      });
    });
  };

  const handleStartChat = () => {
    // Animate modal out
    setIsModalClosing(true);
    
    // After animation completes, hide modal
    setTimeout(() => {
      setShowScenarioModal(false);
      setIsModalClosing(false);
      setPendingCharacterId(null);
    }, 300); // Match animation duration
  };

  // Manual reset for stuck loading state
  const resetLoadingState = () => {
    debugLog('Manually resetting loading state');
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    setIsLoadingProgress(false);
    setHasStartedLoading(false);
  };

  // Global debug function for testing
  window.debugLoadingState = () => {
    debugLog('Current loading state:', {
      isLoadingProgress,
      hasStartedLoading,
      userInfo: !!userInfo,
      userRole: userInfo?.role,
      loadingTimeoutId: loadingTimeoutRef.current
    });
  };

  // Global function to force reset loading
  window.forceResetLoading = resetLoadingState;

  // Get progress for a specific character (used in scenario modal)
  const getCharacterProgressForModal = (characterId) => {
    // If we're currently in a chat session for this character, use the current session progress
    // This ensures consistency - we show the progress from the active session, not stale data
    if (currentCharacter === characterId && messages.length > 0) {
      // Use getJamieProgress() directly since it already handles all the calculation logic correctly
      return getJamieProgress();
    }
    
    // Otherwise, use saved progress from userInfo (for when modal is shown but no active session)
    if (!userInfo?.progress?.[characterId]) return 0;
    
    const charProgress = userInfo.progress[characterId];
    const hasSessions = charProgress.sessions && charProgress.sessions.length > 0;
    
    if (!hasSessions) return 0;
    
    const latestSession = charProgress.lastSession || charProgress.sessions[charProgress.sessions.length - 1];
    if (!latestSession || !latestSession.score) return 0;
    
    // Calculate progress percentage from score
    // Score is a decimal (0-1), convert to percentage (0-100)
    const score = typeof latestSession.score === 'number' ? latestSession.score : 0;
    return Math.round(score * 100);
  };

  // Get character state for modal (used when character has progress)
  const getCharacterStateForModal = (characterId, progress) => {
    const progressDecimal = progress / 100; // Convert percentage to decimal (0-1)
    
    if (characterId === 'andres' || characterId === 'kavya' || characterId === 'alex' || characterId === 'sarah') {
      // Same stage thresholds for all game mode characters
      if (progressDecimal >= 0.8) return 'Visioning';
      if (progressDecimal >= 0.65) return 'Curious';
      if (progressDecimal >= 0.5) return 'Experimenting';
      if (progressDecimal >= 0.3) return 'Exploring';
      if (progressDecimal >= 0.15) return 'Defensive';
      return 'Overwhelmed';
    }
    
    return 'Not Started';
  };

  // Handle logout
  const handleLogout = async () => {
    debugLog('Logout initiated');
    
    try {
      // Use authentication service for logout
      if (USE_SUPABASE_AUTH) {
        debugLog('Calling Supabase logout');
        const result = await supabaseAuthService.logout();
        debugLog('Supabase logout result:', result);
        
        // The auth listener will handle clearing state when it receives the 'logout' event
        // But we'll also clear it here as a backup
        if (result.success) {
          debugLog('Logout successful, clearing local state');
          setUserInfo(null);
          setCurrentView(null);
          setMessages([]);
          setCurrentMessage('');
          setCurrentCharacter('jamie');
          setGameMode('game');
          setAttemptsRemaining(getMaxAttempts('jamie'));
          setAnimatedProgress(0);
          setIsProgressAnimating(false);
          setIsLoadingProgress(false);
          setHasStartedLoading(false);
          setLoadedUserIds(new Set()); // Clear loaded user IDs
          
          // Clear localStorage
          localStorage.removeItem('gameMode');
          localStorage.removeItem('currentCharacter');
          
          // Clear sessionStorage
          sessionStorage.removeItem('currentView');
          localStorage.removeItem('messages');
          localStorage.removeItem('currentSession');
        }
      } else {
        authService.logout();
        // Clear state for non-Supabase auth
        setUserInfo(null);
        setCurrentView(null);
        setMessages([]);
        setCurrentMessage('');
        setCurrentCharacter('jamie');
        setGameMode('game');
        setAttemptsRemaining(getMaxAttempts('jamie'));
        setAnimatedProgress(0);
        setIsProgressAnimating(false);
        setIsLoadingProgress(false);
        
        // Clear localStorage
        localStorage.removeItem('gameMode');
        localStorage.removeItem('currentCharacter');
        localStorage.removeItem('messages');
        localStorage.removeItem('currentSession');
      }
      
      debugLog('Logout completed');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setUserInfo(null);
      setCurrentView(null);
      setMessages([]);
      setCurrentMessage('');
      setIsLoadingProgress(false);
    }
  };

  // Handle settings (placeholder for now)
  const handleSettings = () => {
    setCurrentView('settings');
  };

  // Save in-progress session to localStorage
  const saveInProgressSession = () => {
    if (!userInfo || messages.length === 0) return;
    
    // Clean messages to avoid circular references
    const cleanMessages = messages.map(msg => ({
      id: msg.id,
      message: msg.message,
      isUser: msg.isUser,
      timestamp: msg.timestamp,
      isSessionEnd: msg.isSessionEnd,
      showFinalScore: msg.showFinalScore,
      dqScore: msg.dqScore,
      avgDqScore: msg.avgDqScore,
      sessionId: msg.sessionId,
      userId: msg.userId,
      personaStage: msg.personaStage || null // Include personaStage for avatar selection
      // Exclude userInfo to avoid circular reference
    }));
    
    const sessionData = {
      character: currentCharacter,
      messages: cleanMessages,
      attemptsRemaining: attemptsRemaining,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(`session_${userInfo.id}_${currentCharacter}`, JSON.stringify(sessionData));
  };

  // Load in-progress session
  const loadInProgressSession = (character) => {
    if (!userInfo) return null;
    
    const savedSession = localStorage.getItem(`session_${userInfo.id}_${character}`);
    if (savedSession) {
      return JSON.parse(savedSession);
    }
    return null;
  };

  // Clear in-progress session
  const clearInProgressSession = (character) => {
    if (!userInfo) return;
    localStorage.removeItem(`session_${userInfo.id}_${character}`);
  };

  // Reset session function
  const resetSession = () => {
    // Confirm reset if there are messages
    if (messages.length > 0 && attemptsRemaining > 0) {
      const confirmed = window.confirm('Are you sure you want to reset this session? All progress will be lost.');
      if (!confirmed) return;
    }
    
    clearInProgressSession(currentCharacter);
    setMessages([]);
    setAttemptsRemaining(getMaxAttempts(currentCharacter));
    setCurrentMessage('');
    setIsLoading(false);
    setIsTyping(false);
    setAnimatedProgress(0);
    setIsProgressAnimating(false);
    setIsNewSession(true); // Mark that next message should trigger reset on backend
    setSessionReview(null);
    setSessionReviewStatus('idle');
  };

  // Request LLM-generated personalized session review.
  // Non-blocking: the static SessionSummaryReport renders immediately; once this
  // resolves, the component upgrades to show transcript-specific feedback.
  const requestSessionReview = async (conversationMessages, dqScore, personaName) => {
    try {
      if (!dqScore || typeof dqScore !== 'object') return;
      const transcript = (conversationMessages || [])
        .filter((m) => m && !m.isSessionEnd && typeof m.message === 'string' && m.message.trim().length > 0)
        .map((m) => ({ isUser: !!m.isUser, message: m.message }));
      if (transcript.length < 2) return;

      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const reviewUrl = isDev
        ? 'http://localhost:3001/session-review'
        : 'https://jamie-backend.onrender.com/session-review';

      setSessionReview(null);
      setSessionReviewStatus('loading');

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 45000);
      const response = await fetch(reviewUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript, dqScore, personaName }),
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!response.ok) {
        console.warn('session-review request failed with status', response.status);
        setSessionReviewStatus('failed');
        return;
      }
      const data = await response.json();
      setSessionReview(data);
      setSessionReviewStatus('ready');
    } catch (err) {
      console.warn('session-review request errored:', err?.message || err);
      setSessionReviewStatus('failed');
    }
  };

  // DISABLED: Auto-save effect - we now save explicitly in setMessages callback to avoid race conditions
  // The explicit save ensures we save with the correct attemptsRemaining value
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // useEffect(() => {
  //   if (messages.length > 0) {
  //     saveInProgressSession();
  //   }
  // }, [messages]);

  // Restore chat session from localStorage on refresh when entering chat view
  // CRITICAL: Only restore ONCE when entering chat view, not on every render
  useEffect(() => {
    // Reset the restore flag when switching characters or views
    if (currentView !== 'chat') {
      hasRestoredSessionRef.current = false;
      return;
    }
    
    if (currentView === 'chat' && userInfo && messages.length === 0 && !isLoading && !isTyping && !hasRestoredSessionRef.current) {
      const savedSession = loadInProgressSession(currentCharacter);
      if (savedSession && savedSession.messages && savedSession.messages.length > 0) {
        hasRestoredSessionRef.current = true; // Mark as restored to prevent multiple restores
        debugLog('Restoring chat session from localStorage:', savedSession);
        console.log('[Restore Session] Messages being restored:', savedSession.messages.map((msg, idx) => ({
          index: idx,
          id: msg.id,
          isUser: msg.isUser,
          hasPersonaStage: 'personaStage' in msg,
          personaStage: msg.personaStage,
          allKeys: Object.keys(msg)
        })));
        console.log('[Restore Session] Restoring attemptsRemaining:', savedSession.attemptsRemaining);
        setMessages(savedSession.messages);
        if (savedSession.attemptsRemaining !== undefined) {
          setAttemptsRemaining(savedSession.attemptsRemaining);
        }
      }
    }
  }, [currentView, userInfo, currentCharacter]); // Only run when these change, not on every render

  // Add initial Andres opening message when starting a new chat session
  useEffect(() => {
    // Only add opening message if:
    // 1. We're in chat view
    // 2. Current character is Andres
    // 3. Messages array is empty (new session, not a saved one)
    // 4. Not currently loading
    // 5. No saved session exists (check explicitly to avoid adding after restoration)
    if (
      currentView === 'chat' &&
      currentCharacter === 'andres' &&
      messages.length === 0 &&
      !isLoading &&
      !isTyping &&
      userInfo // Make sure userInfo is available before checking for saved session
    ) {
      // Double-check that no saved session exists
      const savedSession = loadInProgressSession(currentCharacter);
      if (!savedSession || !savedSession.messages || savedSession.messages.length === 0) {
        const andresOpeningMessage = {
          id: `andres-opening-${Date.now()}`,
          message: "Honestly, I'm just wiped out. Every sprint feels the same and I don't even know if switching makes sense. It all feels like too much sometimes.",
          isUser: false,
          timestamp: new Date().toISOString(),
          personaStage: 'overwhelmed' // Opening message is always overwhelmed
        };
        
        setMessages([andresOpeningMessage]);
      }
    }
  }, [currentView, currentCharacter, messages.length, isLoading, isTyping, userInfo]);

  // Add initial Jamie opening message when starting a new chat session
  useEffect(() => {
    // Only add opening message if:
    // 1. We're in chat view
    // 2. Current character is Jamie
    // 3. Messages array is empty (new session, not a saved one)
    // 4. Not currently loading
    // 5. No saved session exists (check explicitly to avoid adding after restoration)
    if (
      currentView === 'chat' &&
      currentCharacter === 'jamie' &&
      messages.length === 0 &&
      !isLoading &&
      !isTyping &&
      userInfo // Make sure userInfo is available before checking for saved session
    ) {
      // Double-check that no saved session exists
      const savedSession = loadInProgressSession(currentCharacter);
      if (!savedSession || !savedSession.messages || savedSession.messages.length === 0) {
        const jamieOpeningMessage = {
          id: `jamie-opening-${Date.now()}`,
          message: "I'm really confused about everything right now. I want to do design but I also don't want to disappoint my parents. I don't even know where to start thinking about this. Every time I try to figure it out, I just feel more stuck. I keep going back and forth between what I want and what feels responsible. It's all just too much pressure right now.",
          isUser: false,
          timestamp: new Date().toISOString(),
          personaStage: 'confused' // Jamie starts at confused stage
        };
        
        setMessages([jamieOpeningMessage]);
      }
    }
  }, [currentView, currentCharacter, messages.length, isLoading, isTyping, userInfo]);

  // Add initial Kavya opening message when starting a new chat session
  useEffect(() => {
    // Only add opening message if:
    // 1. We're in chat view
    // 2. Current character is Kavya
    // 3. Messages array is empty (new session, not a saved one)
    // 4. Not currently loading
    // 5. No saved session exists (check explicitly to avoid adding after restoration)
    if (
      currentView === 'chat' &&
      currentCharacter === 'kavya' &&
      messages.length === 0 &&
      !isLoading &&
      !isTyping &&
      userInfo // Make sure userInfo is available before checking for saved session
    ) {
      // Double-check that no saved session exists
      const savedSession = loadInProgressSession(currentCharacter);
      if (!savedSession || !savedSession.messages || savedSession.messages.length === 0) {
        const kavyaOpeningMessage = {
          id: `kavya-opening-${Date.now()}`,
          message: "Honestly, I'm just exhausted trying to figure out what to do after graduation. I keep going back and forth between the corporate path—you know, stability, benefits, clear career progression—and the idea of starting my own business where I could actually have work-life balance and make a meaningful impact. But every corporate job I look at seems like it'll eat my soul, and starting something on my own feels impossible with student loans and no real experience. I feel like I'm supposed to just know what I want, but I don't. It's all just too much pressure right now.",
          isUser: false,
          timestamp: new Date().toISOString(),
          personaStage: 'overwhelmed' // Kavya starts at overwhelmed stage
        };
        
        setMessages([kavyaOpeningMessage]);
      }
    }
  }, [currentView, currentCharacter, messages.length, isLoading, isTyping, userInfo]);

  // Add initial Alex opening message when starting a new chat session
  useEffect(() => {
    // Only add opening message if:
    // 1. We're in chat view
    // 2. Current character is Alex
    // 3. Messages array is empty (new session, not a saved one)
    // 4. Not currently loading or typing
    // 5. User info is available
    if (
      currentView === 'chat' &&
      currentCharacter === 'alex' &&
      messages.length === 0 &&
      !isLoading &&
      !isTyping &&
      userInfo // Make sure userInfo is available before checking for saved session
    ) {
      // Double-check that no saved session exists
      const savedSession = loadInProgressSession(currentCharacter);
      if (!savedSession || !savedSession.messages || savedSession.messages.length === 0) {
        const alexOpeningMessage = {
          id: `alex-opening-${Date.now()}`,
          message: "Everyone keeps telling me I'm in a great position — good grades, solid scores, college is basically the next step. But honestly, none of it feels exciting. Every application feels like I'm locking into a future I can't even picture yet, and the deadlines are getting closer. I don't know if I should just push through and apply, or if that's a mistake.",
          isUser: false,
          timestamp: new Date().toISOString(),
          personaStage: 'overwhelmed'
        };
        
        setMessages([alexOpeningMessage]);
      }
    }
  }, [currentView, currentCharacter, messages.length, isLoading, isTyping, userInfo]);

  // Add initial Sarah opening message when starting a new chat session
  useEffect(() => {
    // Only add opening message if:
    // 1. We're in chat view
    // 2. Current character is Sarah
    // 3. Messages array is empty (new session, not a saved one)
    // 4. Not currently loading or typing
    // 5. User info is available
    if (
      currentView === 'chat' &&
      currentCharacter === 'sarah' &&
      messages.length === 0 &&
      !isLoading &&
      !isTyping &&
      userInfo // Make sure userInfo is available before checking for saved session
    ) {
      // Double-check that no saved session exists
      const savedSession = loadInProgressSession(currentCharacter);
      if (!savedSession || !savedSession.messages || savedSession.messages.length === 0) {
        const sarahOpeningMessage = {
          id: `sarah-opening-${Date.now()}`,
          message: "Honestly I'm just exhausted trying to figure out what to do. I've been away from work for several years, and now I'm torn between returning to my previous field where I have experience and skills, or starting fresh in a new direction that might better align with where I am in my life now. Every time I think about going back, I worry my skills are outdated and I'll be behind. But starting over feels impossible when I have financial responsibilities and ongoing personal commitments. It's all just too much pressure right now.",
          isUser: false,
          timestamp: new Date().toISOString(),
          personaStage: 'overwhelmed' // Sarah starts at overwhelmed stage
        };
        
        setMessages([sarahOpeningMessage]);
      }
    }
  }, [currentView, currentCharacter, messages.length, isLoading, isTyping, userInfo]);

  // Calculate progress percentage for current character
  const getJamieProgress = () => {
    if (messages.length === 0) return 0; // Start at 0%
    
    // Find the latest character message with DQ score
    const characterMessages = messages.filter(msg => !msg.isUser && msg.dqScore);
    if (characterMessages.length === 0) return 0;
    
    const latestMessage = characterMessages[characterMessages.length - 1];
    
    // Always calculate from dqScore object for more accurate progress display
    // avgDqScore from backend is the minimum (weakest link), which can be misleading
    if (!latestMessage.dqScore) {
      // Fallback to avgDqScore if dqScore is not available
      if (latestMessage.avgDqScore !== undefined) {
        const safeScore = typeof latestMessage.avgDqScore === 'number' && !isNaN(latestMessage.avgDqScore) && isFinite(latestMessage.avgDqScore)
          ? latestMessage.avgDqScore
          : 0;
        return Math.round(safeScore * 100);
      }
      return 0;
    }
    
    const dqObj = latestMessage.dqScore;
    
    // Only use the 6 core DQ dimensions, exclude 'overall' and 'rationale'
    const coreDimensions = ['framing', 'alternatives', 'information', 'values', 'reasoning', 'commitment'];
    const values = coreDimensions
      .map(dim => dqObj[dim])
      .filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v) && v >= 0 && v <= 1);
    
    if (values.length === 0) return 0;
    
    // Use average of top 5 dimensions (or all if less than 5)
    // This gives a more encouraging progress view while still reflecting overall quality
    const sortedValues = [...values].sort((a, b) => b - a); // Sort descending
    const topValues = sortedValues.slice(0, Math.min(5, sortedValues.length));
    const avgScore = topValues.reduce((sum, val) => sum + val, 0) / topValues.length;
    return Math.round(avgScore * 100);
  };

  // Animate progress changes - moved after getJamieProgress definition
  // Use a ref to track the last target progress to avoid dependency issues
  const lastProgressRef = useRef(0);
  const animatedProgressRef = useRef(0); // Track current animated value without triggering re-renders
  
  // Sync ref with state
  useEffect(() => {
    animatedProgressRef.current = animatedProgress;
  }, [animatedProgress]);
  
  useEffect(() => {
    const currentProgress = getJamieProgress();
    // Only animate if progress actually changed (not just during animation)
    if (currentProgress !== lastProgressRef.current) {
      lastProgressRef.current = currentProgress;
      setIsProgressAnimating(true);
      
      // Smooth animation to new progress value
      const startProgress = animatedProgressRef.current; // Use ref to get current value
      const endProgress = currentProgress;
      const duration = 1200; // 1.2 seconds
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = startProgress + (endProgress - startProgress) * easeOutCubic;
        const roundedValue = Math.round(currentValue);
        
        setAnimatedProgress(roundedValue);
        animatedProgressRef.current = roundedValue; // Update ref as we animate
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsProgressAnimating(false);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [messages]); // Only depend on messages - this ensures we react to new DQ scores immediately

  // Helper function to get effective stage for a message
  // If message doesn't have personaStage, look forward/backward to find it
  const getEffectiveStage = (messageIndex, messages) => {
    const msg = messages[messageIndex];
    
    // If this message has a stage, use it
    if (msg && msg.personaStage) {
      return msg.personaStage;
    }
    
    // Look forward to find next message with a stage
    for (let i = messageIndex + 1; i < messages.length; i++) {
      if (!messages[i].isUser && messages[i].personaStage) {
        return messages[i].personaStage;
      }
    }
    
    // Look backward to find previous message with a stage
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (!messages[i].isUser && messages[i].personaStage) {
        return messages[i].personaStage;
      }
    }
    
    // Default to overwhelmed if no stage found
    return null;
  };

  // Get avatar image path based on character and stage
  const getAvatarImage = (character, stage = null, message = null, messageIndex = null) => {
    // For Andres, use stage-based images
    if (character === 'andres') {
      // Normalize stage to lowercase for consistent matching
      const normalizedStage = stage ? stage.toLowerCase().trim() : null;
      
      // Map stage names to image filenames (API returns lowercase)
      const stageMap = {
        'overwhelmed': 'Overwhelmed',
        'defensive': 'Defensive',
        'exploring': 'Exploring',
        'experimenting': 'Experimenting',
        'curious': 'Curious',
        'visioning': 'Envisioning' // Note: file is "Envisioning" but stage is "visioning"
      };
      
      // If no stage provided, default to Overwhelmed for empty state
      const imageStage = normalizedStage ? (stageMap[normalizedStage] || 'Overwhelmed') : 'Overwhelmed';
      
      // Debug logging (can be removed later)
      if (process.env.NODE_ENV === 'development') {
        console.log('[Avatar] Character:', character, 'Stage:', stage, 'Normalized:', normalizedStage, 'Image:', imageStage, 'messageIndex:', messageIndex);
      }
      
      return `/images/DC Images/Andres/Andres_${imageStage}.png`;
    }
    
    // For Kavya, use stage-based images (same stages as Andres)
    if (character === 'kavya') {
      // Normalize stage to lowercase for consistent matching
      const normalizedStage = stage ? stage.toLowerCase().trim() : null;
      
      // Map stage names to image filenames (Kavya uses "Visioning" not "Envisioning")
      const stageMap = {
        'overwhelmed': 'Overwhelmed',
        'defensive': 'Defensive',
        'exploring': 'Exploring',
        'experimenting': 'Experimenting',
        'curious': 'Curious',
        'visioning': 'Visioning' // Note: Kavya file is "Visioning" (not "Envisioning")
      };
      
      // If no stage provided, default to Overwhelmed for empty state
      const imageStage = normalizedStage ? (stageMap[normalizedStage] || 'Overwhelmed') : 'Overwhelmed';
      
      // Debug logging (can be removed later)
      if (process.env.NODE_ENV === 'development') {
        console.log('[Avatar] Character:', character, 'Stage:', stage, 'Normalized:', normalizedStage, 'Image:', imageStage, 'messageIndex:', messageIndex);
      }
      
      return `/images/DC Images/Kavya/Kavya_${imageStage}.png`;
    }
    
    // For Alex, use stage-based images (same stages as Andres/Kavya, using Persona4 images)
    if (character === 'alex') {
      // Normalize stage to lowercase for consistent matching
      const normalizedStage = stage ? stage.toLowerCase().trim() : null;
      
      // Map stage names to image filenames (Alex uses Persona4 images)
      const stageMap = {
        'overwhelmed': 'Overwhelmed',
        'defensive': 'Defensive',
        'exploring': 'Exploring',
        'experimenting': 'Experimenting',
        'curious': 'Curious',
        'visioning': 'Visioning'
      };
      
      // If no stage provided, default to Overwhelmed for empty state
      const imageStage = normalizedStage ? (stageMap[normalizedStage] || 'Overwhelmed') : 'Overwhelmed';
      
      // Debug logging (can be removed later)
      if (process.env.NODE_ENV === 'development') {
        console.log('[Avatar] Character:', character, 'Stage:', stage, 'Normalized:', normalizedStage, 'Image:', imageStage, 'messageIndex:', messageIndex);
      }
      
      return `/images/DC Images/Persona4/Persona4_${imageStage}.png`;
    }
    
    // For Sarah, use stage-based images (Persona5 images)
    if (character === 'sarah') {
      // Normalize stage to lowercase for consistent matching
      const normalizedStage = stage ? stage.toLowerCase().trim() : null;
      
      // Map stage names to image filenames (Sarah uses Persona5 images)
      // Note: Some files use "Person5_" prefix (Curious, Overwhelmed) while others use "Persona5_"
      const stageMap = {
        'overwhelmed': 'Overwhelmed',
        'defensive': 'Defensive',
        'exploring': 'Exploring',
        'experimenting': 'Experimenting',
        'curious': 'Curious',
        'visioning': 'Visioning'
      };
      
      // If no stage provided, default to Overwhelmed for empty state
      const imageStage = normalizedStage ? (stageMap[normalizedStage] || 'Overwhelmed') : 'Overwhelmed';
      
      // Debug logging (can be removed later)
      if (process.env.NODE_ENV === 'development') {
        console.log('[Avatar] Character:', character, 'Stage:', stage, 'Normalized:', normalizedStage, 'Image:', imageStage, 'messageIndex:', messageIndex);
      }
      
      // Handle naming inconsistency: Curious and Overwhelmed use "Person5_" prefix, others use "Persona5_"
      const baseName = (imageStage === 'Curious' || imageStage === 'Overwhelmed') ? 'Person5' : 'Persona5';
      return `/images/DC Images/Persona5/${baseName}_${imageStage}.png`;
    }
    
    // Default avatars for other characters
    if (character === 'jamie') return "/images/DC Images/Jamie/Jamie_LandingPage.png";
    return "/images/DC Images/Jamie/Jamie_LandingPage.png"; // Default
  };

  // Get character's current state based on progress percentage
  const getCharacterState = () => {
    const progress = getJamieProgress();
    const progressDecimal = progress / 100; // Convert percentage to decimal (0-1)
    
    // Use character-specific stage thresholds
    if (currentCharacter === 'andres') {
      // Andres stages: overwhelmed (0), defensive (0.15), exploring (0.3), experimenting (0.5), curious (0.65), visioning (0.8)
      // VERSION CHECK: If you see defensive: 0.15, exploring: 0.3, experimenting: 0.5 in console, the update is deployed
      if (progressDecimal >= 0.8) return 'Visioning';
      if (progressDecimal >= 0.65) return 'Curious';
      if (progressDecimal >= 0.5) return 'Experimenting';
      if (progressDecimal >= 0.3) return 'Exploring';
      if (progressDecimal >= 0.15) return 'Defensive';
      return 'Overwhelmed';
    } else if (currentCharacter === 'kavya') {
      // Kavya stages: overwhelmed (0), defensive (0.15), exploring (0.3), experimenting (0.5), curious (0.65), visioning (0.8)
      // Same thresholds as Andres
      if (progressDecimal >= 0.8) return 'Visioning';
      if (progressDecimal >= 0.65) return 'Curious';
      if (progressDecimal >= 0.5) return 'Experimenting';
      if (progressDecimal >= 0.3) return 'Exploring';
      if (progressDecimal >= 0.15) return 'Defensive';
      return 'Overwhelmed';
    } else if (currentCharacter === 'alex') {
      // Alex stages: overwhelmed (0), defensive (0.15), exploring (0.3), experimenting (0.5), curious (0.65), visioning (0.8)
      // Same thresholds as Andres/Kavya
      if (progressDecimal >= 0.8) return 'Visioning';
      if (progressDecimal >= 0.65) return 'Curious';
      if (progressDecimal >= 0.5) return 'Experimenting';
      if (progressDecimal >= 0.3) return 'Exploring';
      if (progressDecimal >= 0.15) return 'Defensive';
      return 'Overwhelmed';
    } else if (currentCharacter === 'sarah') {
      // Sarah stages: overwhelmed (0), defensive (0.15), exploring (0.3), experimenting (0.5), curious (0.65), visioning (0.8)
      // Same thresholds as Andres/Kavya/Daniel
      if (progressDecimal >= 0.8) return 'Visioning';
      if (progressDecimal >= 0.65) return 'Curious';
      if (progressDecimal >= 0.5) return 'Experimenting';
      if (progressDecimal >= 0.3) return 'Exploring';
      if (progressDecimal >= 0.15) return 'Defensive';
      return 'Overwhelmed';
    } else {
      // Jamie stages: confused (0), uncertain (0.3), thoughtful (0.6), confident (0.8)
      if (progressDecimal >= 0.8) return 'Confident';
      if (progressDecimal >= 0.6) return 'Thoughtful';
      if (progressDecimal >= 0.3) return 'Uncertain';
      return 'Confused';
    }
  };

  // Test connection to backend
  const testConnection = async () => {
    setConnectionStatus('testing');
    
    // Try local backend first (for development)
    const localBackend = 'http://localhost:3001/chat';
    const remoteBackend = 'https://jamie-backend.onrender.com/chat';
    
    // Check if we're in development (localhost)
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendUrl = isDevelopment ? localBackend : remoteBackend;
    
    console.log('🔍 Testing backend connection to:', backendUrl);
    
    try {
      let response;
      try {
        response = await fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ message: "test connection" }),
        });
      } catch (fetchError) {
        // Handle network errors (CORS, connection refused, 503, etc.)
        console.error('❌ Network error testing backend connection:', fetchError);
        if (fetchError.message.includes('503') || fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Load failed')) {
          console.warn('⚠️ Backend service is unavailable (503). This may be because:');
          console.warn('   - Render.com service is sleeping (free tier)');
          console.warn('   - Service is restarting or down');
          console.warn('   - Network connectivity issue');
        }
        throw fetchError;
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.jamie_reply) {
          setConnectionStatus('connected');
          console.log('✅ Backend connection successful!');
          debugLog('✅ Backend connection successful');
          return;
        }
      }
      
      // Handle 503 specifically
      if (response.status === 503) {
        console.warn('⚠️ Backend returned 503 (Service Unavailable)');
        throw new Error('Backend service is temporarily unavailable (503)');
      }
      
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      console.error('❌ Backend connection failed:', error);
      console.log('⚠️ Backend is unavailable. Status will be checked again when you send a message.');
      
      // Backend is unavailable - don't switch to demo mode, just mark as failed
      debugLog('❌ Backend unavailable');
      setConnectionStatus('failed');
      // Don't automatically switch to demo mode - let user wait for backend to come back
    }
  };

  // Demo mode responses
  const getDemoResponse = (message) => {
    const responses = [
      "I really appreciate you asking about this... it's been weighing on my mind a lot. My parents sacrificed so much to come here and they always talked about me becoming an engineer. But when I'm in my design classes, I just feel... alive, you know?",
      
      "It's really hard because I see the disappointment in their eyes when I even mention art. They keep saying 'art doesn't pay the bills' and 'we didn't come to America for you to struggle as an artist.' But I'm struggling now too, just in a different way.",
      
      "Sometimes I wonder if I'm being selfish. Like, maybe I should just push through engineering for them? But then I think about spending my whole life doing something that doesn't inspire me... that feels wrong too.",
      
      "You know what's funny? When I was little, my mom used to love my drawings. She'd put them on the fridge and brag to her friends. But somewhere along the way, that changed to 'drawing is just a hobby, focus on real subjects.'",
      
      "I've been looking at some art schools and design programs, and there are actually really good career prospects. UX design, industrial design, even engineering roles that are more creative. But how do I even start that conversation at home?",
      
      "The guilt is probably the hardest part. Like, they worked so hard to give me opportunities, and here I am wanting to 'throw it away' for something they see as frivolous. But I don't think they understand that I'm not throwing anything away - I'm trying to find where I actually belong.",
      
      "I've been doing some research on successful artists and designers, and many of them actually have technical backgrounds. Maybe there's a way to honor both parts of myself? I just don't know how to explain that to my parents without them thinking I'm making excuses.",
      
      "What really gets to me is when they compare me to my cousin who's doing well in computer science. They're like 'why can't you just be practical like her?' But they don't see how miserable I am in my current classes, or how excited I get when I'm working on creative projects."
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  };

  const getDemoScore = () => ({
    framing: Math.random() * 0.3 + 0.65,
    alternatives: Math.random() * 0.3 + 0.55,
    information: Math.random() * 0.3 + 0.60,
    values: Math.random() * 0.3 + 0.70,
    reasoning: Math.random() * 0.3 + 0.62,
    commitment: Math.random() * 0.3 + 0.58
  });

  // Scroll to bottom when loading a saved session
  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages.length, isLoading]);


  // Test connection on mount
  useEffect(() => {
    if (!demoMode) {
      testConnection();
    }
  }, [demoMode]);

  // User info form component - REMOVED (using LandingPage instead)
  // eslint-disable-next-line no-unused-vars
  const UserInfoModal_OLD = () => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      affiliation: ''
    });

    const handleSubmit = () => {
      if (!formData.name || !formData.email) return;
      setUserInfo(formData);
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-[#2C73EB] flex items-end justify-center mx-auto mb-4 shadow-lg overflow-hidden">
              <img 
                src="/images/DC Images/Jamie/Jamie_LandingPage.png" 
                alt="Jamie" 
                className="w-16 h-16 object-cover object-bottom"
              />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Welcome to Decision Coach</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              You'll be coaching Jamie, a sophomore mechanical engineering student considering switching to art/design. 
              Please provide some basic information to get started.
            </p>
          </div>
          
          {/* Mode Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              Select Mode *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setGameMode('game')}
                className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                  gameMode === 'game' 
                    ? 'border-[#2C73EB] bg-blue-50 text-[#2C73EB]' 
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="text-sm font-semibold">Game</div>
                <div className="text-xs text-gray-500 mt-1">With progress tracking</div>
              </button>
              <button
                onClick={() => setGameMode('assessment')}
                className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                  gameMode === 'assessment' 
                    ? 'border-[#2C73EB] bg-blue-50 text-[#2C73EB]' 
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="text-sm font-semibold">Assessment</div>
                <div className="text-xs text-gray-500 mt-1">Clean evaluation mode</div>
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2C73EB] focus:border-transparent bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-500"
                placeholder="Your name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2C73EB] focus:border-transparent bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-500"
                placeholder="your.email@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                Affiliation
              </label>
              <input
                type="text"
                value={formData.affiliation}
                onChange={(e) => setFormData({...formData, affiliation: e.target.value})}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2C73EB] focus:border-transparent bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-500"
                placeholder="University, Company, etc."
              />
            </div>
            
            
            <button
              onClick={handleSubmit}
              disabled={!formData.name || !formData.email}
              className="w-full bg-[#2C73EB] text-white py-3 px-6 rounded-lg hover:bg-[#1e5bb8] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Start Coaching Session
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Decision Quality Score visualization - now only shows coaching effectiveness
  // eslint-disable-next-line no-unused-vars
  const DQScoreDisplay = ({ scores, avgDqScore, className = "" }) => {
    const dimensions = [
      { key: 'framing', label: 'Framing', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { key: 'alternatives', label: 'Alternatives', color: 'bg-gradient-to-r from-emerald-500 to-emerald-600' },
      { key: 'information', label: 'Information', color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
      { key: 'values', label: 'Values', color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      { key: 'reasoning', label: 'Reasoning', color: 'bg-gradient-to-r from-teal-500 to-teal-600' },
      { key: 'commitment', label: 'Commitment', color: 'bg-gradient-to-r from-indigo-500 to-indigo-600' }
    ];

    // Use the "weakest link" principle - DQ score is the minimum of all components
    const avgScore = Math.min(...Object.values(scores));

    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Star className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-sm font-bold text-gray-800">Coaching Effectiveness</h3>
        </div>
        
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200/50">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-medium text-gray-700">Decision Quality Score</span>
            <span className="text-sm font-medium text-gray-800">{avgScore.toFixed(1)}/1.0</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {dimensions.map(dim => (
              <div key={dim.key} className="space-y-2">
                <div className="flex justify-between text-xs font-medium text-gray-700">
                  <span>{dim.label}</span>
                  <span className="text-gray-600">{scores[dim.key]?.toFixed(1)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <div
                    className={`${dim.color} h-3 rounded-full transition-all duration-500 shadow-sm`}
                    style={{ width: `${scores[dim.key] * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Individual message component
  // eslint-disable-next-line no-unused-vars
  const ChatMessage = ({ message, isUser, dqScore, avgDqScore, timestamp, isError, showDemoButton, onMessageClick, showDqScore }) => {
    // Determine Jamie's current state for progress display
    const getJamieState = (score) => {
      if (score >= 0.8) return { label: 'Confident', color: 'text-green-600', bg: 'bg-green-50' };
      if (score >= 0.6) return { label: 'Thoughtful', color: 'text-blue-600', bg: 'bg-blue-50' };
      if (score >= 0.3) return { label: 'Uncertain', color: 'text-yellow-600', bg: 'bg-yellow-50' };
      return { label: 'Confused', color: 'text-red-600', bg: 'bg-red-50' };
    };

    const jamieState = !isUser ? getJamieState(avgDqScore || 0) : null;

    return (
      <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'} mb-6 overflow-hidden`}>
        {!isUser && (
          <JamieFace dqScore={dqScore} avgDqScore={avgDqScore} />
        )}
        
        <div className={`max-w-[85%] ${isUser ? 'order-2' : ''} min-w-0 flex-shrink`}>
          <div
            className={`p-4 rounded-3xl shadow-sm ${
              isUser
                ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white ml-auto shadow-blue-200/50'
                : isError
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-white text-gray-800 shadow-gray-200/50 border border-gray-100 cursor-pointer hover:shadow-md transition-shadow'
            }`}
            onClick={!isUser && onMessageClick ? onMessageClick : undefined}
          >
            <p className="whitespace-pre-wrap text-sm leading-relaxed break-words overflow-hidden word-wrap-anywhere">{message}</p>
            
            {/* Jamie's Progress - show in all Jamie's messages */}
            {!isUser && jamieState && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Jamie's Progress</span>
                  <span className="text-sm font-medium text-gray-800">{jamieState.label} • {((avgDqScore || 0) * 100).toFixed(0)}%</span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      avgDqScore >= 0.8 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                      avgDqScore >= 0.6 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                      avgDqScore >= 0.3 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                      'bg-gradient-to-r from-red-500 to-red-600'
                    }`}
                    style={{ width: `${(avgDqScore || 0) * 100}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Confused</span>
                  <span>Uncertain</span>
                  <span>Thoughtful</span>
                  <span>Confident</span>
                </div>
                
                {/* Coaching Effectiveness - show when clicked and in game mode */}
                {dqScore && showDqScore && characterData[currentCharacter].gameMode === 'game' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <Star className="w-3 h-3 text-white" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-800">Coaching Effectiveness</h3>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200/50">
                      <div className="mb-3">
                        <span className="text-xs font-medium text-gray-700">Decision Quality Score: </span>
                        <span className="text-sm font-medium text-gray-800">{(avgDqScore !== undefined ? avgDqScore : safeMinDqScore(dqScore)).toFixed(1)}/1.0</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { key: 'framing', label: 'Framing', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
                          { key: 'alternatives', label: 'Alternatives', color: 'bg-gradient-to-r from-emerald-500 to-emerald-600' },
                          { key: 'information', label: 'Information', color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
                          { key: 'values', label: 'Values', color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
                          { key: 'reasoning', label: 'Reasoning', color: 'bg-gradient-to-r from-teal-500 to-teal-600' },
                          { key: 'commitment', label: 'Commitment', color: 'bg-gradient-to-r from-indigo-500 to-indigo-600' }
                        ].map(dim => (
                          <div key={dim.key} className="space-y-1">
                            <div className="flex justify-between text-xs font-medium text-gray-700">
                              <span>{dim.label}</span>
                              <span className="text-gray-600">{dqScore[dim.key]?.toFixed(1)}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                              <div
                                className={`${dim.color} h-2 rounded-full transition-all duration-500 shadow-sm`}
                                style={{ width: `${dqScore[dim.key] * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {showDemoButton && (
              <button
                onClick={() => setDemoMode(true)}
                className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Try Demo Mode
              </button>
            )}
          </div>
          
          <div className={`text-xs text-gray-500 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
            {new Date(timestamp).toLocaleTimeString()}
          </div>
        </div>
        
        {isUser && (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0 order-3 shadow-md">
            <User className="w-5 h-5 text-blue-600" />
          </div>
        )}
      </div>
    );
  };

  // Typing indicator
  // eslint-disable-next-line no-unused-vars
  const TypingIndicator = () => (
    <div className="flex gap-3 justify-start mb-6 overflow-hidden">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center flex-shrink-0 shadow-md">
        <Bot className="w-5 h-5 text-pink-600" />
      </div>
      <div className="bg-white text-gray-800 p-5 rounded-3xl shadow-sm border border-gray-100 max-w-[85%] min-w-0 flex-shrink">
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
          <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
          <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
        </div>
      </div>
    </div>
  );

  // Send message function
  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoading || attemptsRemaining <= 0) return;

    const messageText = currentMessage.trim();
    setCurrentMessage('');
    
    // CRITICAL: Check if this is a new session BEFORE adding user message
    // If messages array is empty OR isNewSession flag is set, this is a reset/new session
    const wasEmptyBeforeMessage = messages.length === 0;
    const shouldReset = isNewSession || wasEmptyBeforeMessage;
    
    // DON'T decrement optimistically - the backend will return the correct turnsRemaining value
    // The backend is the source of truth for attempts remaining
    // We'll update attemptsRemaining when we receive the backend response
    
    // Clear the new session flag after using it
    if (shouldReset) {
      setIsNewSession(false);
    }
    
    // Add user message immediately with smooth transition
    const userMessage = {
      id: Date.now(),
      message: messageText,
      isUser: true,
      timestamp: new Date().toISOString(),
      userInfo
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    
    // Small delay before showing loading state for smoother transition
    setTimeout(() => {
      setIsLoading(true);
      setIsTyping(true);
      
      // Scroll to the user's message during loading
      setTimeout(() => {
        const userMessageElement = document.querySelector('[data-message-id="' + userMessage.id + '"]');
        if (userMessageElement && chatContainerRef.current) {
          const messageOffsetTop = userMessageElement.offsetTop;
          chatContainerRef.current.scrollTo({
            top: messageOffsetTop,
            behavior: 'smooth'
          });
        }
      }, 100);
      
      // Start thinking text animation with smooth transitions
      // Persona-specific loading messages that match each character's vibe
      const getThinkingStates = (character) => {
        switch(character) {
          case 'jamie':
            // Jamie: confused, uncertain student - casual, uncertain, overwhelmed
            return ['Hmm...', 'I dunno...', 'Let me think...', 'Maybe...', 'I guess...', 'Um...'];
          case 'andres':
            // Andres: burnt out engineer - practical, measured, slightly defensive
            return ['Hmm', 'Let me see...', 'Well...', 'I suppose...', 'Actually...', 'Right...'];
          case 'kavya':
            // Kavya: recent graduate exploring - curious, thoughtful, exploratory
            return ['Hmm...', 'Interesting...', 'Let me consider...', 'I wonder...', 'That\'s a good point...', 'Maybe...'];
          default:
            // Default: more natural, less robotic
            return ['Hmm...', 'Let me think...', 'I see...', 'Hmm', 'Well...'];
        }
      };
      
      const thinkingStates = getThinkingStates(currentCharacter);
      let currentIndex = 0;
      const thinkingInterval = setInterval(() => {
        // Fade out
        setIsTextTransitioning(true);
        
        // Change text after fade out
        setTimeout(() => {
          setThinkingText(thinkingStates[currentIndex]);
          currentIndex = (currentIndex + 1) % thinkingStates.length;
          
          // Fade in
          setTimeout(() => {
            setIsTextTransitioning(false);
          }, 50);
        }, 150);
      }, 1200);
      
      // Store interval ID to clear later
      window.thinkingInterval = thinkingInterval;
    }, 150);

    // Handle demo mode
    if (demoMode) {
      setTimeout(() => {
        const demoDqScore = getDemoScore();
        const jamieReply = getDemoResponse(messageText);
        
        // Calculate DQ score using "weakest link" principle for demo
        const demoAvgDqScore = Math.min(...Object.values(demoDqScore));
        
        // Update user message with demo DQ score
        setMessages(prev => prev.map(msg => 
          msg.id === userMessage.id 
            ? { ...msg, dqScore: demoDqScore, avgDqScore: demoAvgDqScore }
            : msg
        ));

        // Add Jamie's demo response
        const jamieMessage = {
          id: Date.now() + 1,
          message: jamieReply,
          isUser: false,
          dqScore: demoDqScore,
          avgDqScore: demoAvgDqScore,
          timestamp: new Date().toISOString(),
          sessionId: "demo-session",
          userId: "demo-user",
          personaStage: null // Demo mode doesn't have stages
        };

        setMessages(prev => [...prev, jamieMessage]);
        setIsLoading(false);
        setIsTyping(false);
        
        // Clear thinking animation
        if (window.thinkingInterval) {
          clearInterval(window.thinkingInterval);
          window.thinkingInterval = null;
        }
        
        // Check if session should end after Jamie's demo response
        if (attemptsRemaining - 1 <= 0) {
          setTimeout(() => {
            // Get the latest DQ scores
            const jamieMessages = messages.filter(msg => !msg.isUser && msg.dqScore);
            const latestMessage = jamieMessages.length > 0 ? jamieMessages[jamieMessages.length - 1] : null;
            const finalDqScore = latestMessage?.dqScore || demoDqScore;
            
            // Check if user achieved 0.7 or higher score
            const currentProgress = getJamieProgress();
            const hasWon = currentProgress >= 70; // 70% = 0.7 score
            
            // Different messages for assessment vs game mode
            const isAssessment = characterData[currentCharacter].gameMode === 'assessment';
            
            const personaName = characterData[currentCharacter].name;
            const sessionEndMessage = {
              id: Date.now() + 2,
              message: isAssessment
                ? hasWon
                  ? `Nicely done! You completed ${personaName}'s assessment with a strong performance. Here's a breakdown of your coaching across the six dimensions of decision quality:`
                  : `Assessment complete. You've finished ${personaName}'s coaching session. Here's a breakdown of your coaching across the six dimensions of decision quality:`
                : hasWon
                  ? `Nicely done! You reached ${currentProgress}% progress with ${personaName}. Here's a breakdown of your coaching across the six dimensions of decision quality:`
                  : `Session complete. You've used all ${getMaxAttempts(currentCharacter)} attempts with ${personaName}. Here's a breakdown of your coaching across the six dimensions of decision quality:`,
              isUser: false,
              timestamp: new Date().toISOString(),
              isSessionEnd: true,
              hasWon: hasWon,
              dqScore: finalDqScore,
              showFinalScore: true,
              personaName: personaName
            };
            
            // Update user progress
            if (userInfo) {
              const rawFinalScore = finalDqScore ? Math.min(...Object.values(finalDqScore)) : 0; // Minimum DQ score (coach sees)
              console.log('💾 Saving session progress (demo path):', {
                character: currentCharacter,
                rawFinalScore,
                finalDqScore,
                hasFinalDqScore: !!finalDqScore,
                dqScoreValues: finalDqScore ? Object.values(finalDqScore) : []
              });
              const personaStage = null;
              const progressData = {
                finalScore: rawFinalScore,
                rawScore: rawFinalScore,
                stage: personaStage,
                attemptsUsed: getMaxAttempts(currentCharacter), // All attempts were used when session ends
                mode: characterData[currentCharacter].gameMode,
                dqScores: finalDqScore,
                completed: characterData[currentCharacter].gameMode === 'assessment' ? true : (rawFinalScore >= 0.7),
                messages: [...messages, sessionEndMessage] // Include the full chat transcript with end message
              };
              // Use the correct auth service based on USE_SUPABASE_AUTH flag
              // Await progress update and refresh userInfo to update tooltip with latest score
              (async () => {
                try {
                  if (USE_SUPABASE_AUTH) {
                    const result = await supabaseAuthService.updateProgress(currentCharacter, progressData);
                    console.log('💾 Progress update result (demo):', result);
                    // Refresh userInfo after progress is saved
                    const updatedUser = supabaseAuthService.getCurrentUser();
                    if (updatedUser) {
                      const jamieProgress = updatedUser.progress?.jamie;
                      const lastSession = jamieProgress?.lastSession;
                      console.log('💾 Updated user progress (demo):', {
                        hasJamieProgress: !!jamieProgress,
                        lastSessionScore: lastSession?.score,
                        lastSessionRawScore: lastSession?.rawScore,
                        sessionsCount: jamieProgress?.sessions?.length
                      });
                      setUserInfo(updatedUser);
                    }
                  } else {
                    authService.updateProgress(currentCharacter, progressData);
                    // Refresh userInfo after progress is saved (AuthService is synchronous)
                    const updatedUser = authService.getCurrentUser();
                    if (updatedUser) {
                      setUserInfo(updatedUser);
                    }
                  }
                } catch (error) {
                  console.error('💾 Error updating progress (demo):', error);
                }
              })();
              
              // Clear in-progress session since it's complete
              clearInProgressSession(currentCharacter);
            }
            
            setMessages(prev => [...prev, sessionEndMessage]);
            if (finalDqScore) {
              requestSessionReview([...messages, sessionEndMessage], finalDqScore, personaName);
            }
          }, 1000);
        }
      }, 1500 + Math.random() * 1000);
      return;
    }

    try {
      debugLog('Sending message to API:', messageText);
      
      // Determine backend URL based on environment
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const backendUrl = isDevelopment ? 'http://localhost:3001/chat' : 'https://jamie-backend.onrender.com/chat';
      
      // Prepare request body with user information
      // CRITICAL: Include character in session_id to prevent cross-contamination between personas
      const sessionId = userInfo 
        ? `session-${userInfo.id}-${currentCharacter}` 
        : `anon-session-${currentCharacter}`;
      
      // CRITICAL: Use shouldReset that was determined BEFORE adding user message
      const requestBody = {
        message: messageText,
        session_id: sessionId,
        user_id: userInfo ? userInfo.id : 'anon-user',
        character: currentCharacter,
        reset: shouldReset // Send reset flag if this is a new session
      };
      
      debugLog('Request body:', requestBody);
      
      console.log('[API Call] Sending request to:', backendUrl, 'Body:', requestBody);
      const apiCallStartTime = Date.now();
      
      let response;
      try {
        response = await fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
      } catch (fetchError) {
        // Handle network errors (CORS, connection refused, 503, etc.)
        console.error('❌ Network error connecting to backend:', fetchError);
        if (fetchError.message.includes('503') || fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Load failed')) {
          throw new Error('Backend service is unavailable (503). The backend may be sleeping or down. Please try again in a moment.');
        }
        throw fetchError;
      }

      const apiCallDuration = Date.now() - apiCallStartTime;
      debugLog('Response status:', response.status);
      console.log('[API Call] Response received, status:', response.status, 'Duration:', apiCallDuration + 'ms');

      if (!response.ok) {
        // Handle 503 specifically
        if (response.status === 503) {
          const errorText = await response.text().catch(() => 'Service Unavailable');
          console.error('❌ Backend returned 503 (Service Unavailable):', errorText);
          throw new Error('Backend service is temporarily unavailable (503). The service may be sleeping or restarting. Please try again in a moment.');
        }
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      debugLog('API Response:', data);
      console.log('[API Response] Full data:', {
        persona_stage: data.persona_stage,
        character: currentCharacter,
        hasPersonaStage: 'persona_stage' in data,
        allKeys: Object.keys(data),
        fullResponse: data, // Log full response for debugging
        turnsRemaining: data.turnsRemaining,
        turnsUsed: data.turnsUsed,
        effectiveTurnsRemaining: data.effectiveTurnsRemaining
      });
      
      // Warn if persona_stage is missing for Andres
      if (currentCharacter === 'andres' && !data.persona_stage) {
        console.warn('⚠️ [API Response] persona_stage is MISSING for Andres! Backend should return this. Response keys:', Object.keys(data));
      }
      
      // Update user message with DQ score components and minimum
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id 
          ? { ...msg, dqScore: data.dq_score, avgDqScore: data.dq_score_minimum }
          : msg
      ));

      // Add Jamie's response
      // For Andres/Kavya, use current progress-based stage to match progress bar
      // Calculate progress from the new message's DQ score (matching getJamieProgress logic)
      let messagePersonaStage = data.persona_stage || null;
      if (currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') {
        // Calculate progress from DQ score (matching getJamieProgress logic)
        // Game mode personas use average of top 5 dimensions
        let progressDecimal = 0;
        if (data.dq_score && typeof data.dq_score === 'object') {
          const coreDimensions = ['framing', 'alternatives', 'information', 'values', 'reasoning', 'commitment'];
          const values = coreDimensions
            .map(dim => data.dq_score[dim])
            .filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v) && v >= 0 && v <= 1);
          
          if (values.length > 0) {
            // Use average of top 5 dimensions for game mode personas
            const sortedValues = [...values].sort((a, b) => b - a); // Sort descending
            const topValues = sortedValues.slice(0, Math.min(5, sortedValues.length));
            progressDecimal = topValues.reduce((sum, val) => sum + val, 0) / topValues.length;
          }
        } else if (typeof data.dq_score_minimum === 'number') {
          progressDecimal = data.dq_score_minimum;
        }
        
        // Determine stage based on progress (matching getCharacterState logic)
        if (progressDecimal >= 0.8) messagePersonaStage = 'visioning';
        else if (progressDecimal >= 0.65) messagePersonaStage = 'curious';
        else if (progressDecimal >= 0.5) messagePersonaStage = 'experimenting';
        else if (progressDecimal >= 0.3) messagePersonaStage = 'exploring';
        else if (progressDecimal >= 0.15) messagePersonaStage = 'defensive';
        else messagePersonaStage = 'overwhelmed';
        
        console.log('[Message Stage] Calculated stage from DQ score:', {
          dqScore: data.dq_score,
          dqScoreMinimum: data.dq_score_minimum,
          progressDecimal: progressDecimal,
          progressPercent: Math.round(progressDecimal * 100),
          stage: messagePersonaStage
        });
      }
      
      const jamieMessage = {
        id: Date.now() + 1,
        message: data.jamie_reply,
        isUser: false,
        dqScore: data.dq_score,
        avgDqScore: data.dq_score_minimum,
        timestamp: data.timestamp,
        sessionId: data.session_id,
        userId: data.user_id,
        personaStage: messagePersonaStage // Store persona stage for avatar selection (progress-based for Andres/Kavya)
      };
      
      console.log('[Message Created] personaStage:', jamieMessage.personaStage, 'message id:', jamieMessage.id, 'hasPersonaStage:', 'personaStage' in jamieMessage, 'progress:', getJamieProgress() + '%');
      
      if ((currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') && !jamieMessage.personaStage) {
        console.error(`❌ [Message Created] CRITICAL: ${currentCharacter} message created WITHOUT personaStage! This will cause avatar issues.`);
      }

      // Update attempts remaining from backend response
      console.log(`[Attempts Update] currentCharacter="${currentCharacter}", data.turnsRemaining=${data.turnsRemaining}, data.turnsUsed=${data.turnsUsed}`);
      console.log(`[Attempts Update] CURRENT attemptsRemaining state BEFORE update:`, attemptsRemaining);
      
      if (data.turnsRemaining !== undefined) {
        // If waiting for final response (closing message just sent), ensure user can still respond
        if (data.isWaitingForFinalResponse) {
          console.log(`[Attempts Update] isWaitingForFinalResponse=true, setting to 1`);
          setAttemptsRemaining(1); // Allow one more response
        } else {
          // CRITICAL: Validate turnsRemaining doesn't exceed max for this character
          const maxForCharacter = getMaxAttempts(currentCharacter);
          const validatedTurnsRemaining = Math.min(data.turnsRemaining, maxForCharacter);
          if (validatedTurnsRemaining !== data.turnsRemaining) {
            console.warn(`⚠️ Backend returned turnsRemaining=${data.turnsRemaining} for ${currentCharacter}, but max is ${maxForCharacter}. Using ${validatedTurnsRemaining}.`);
          }
          console.log(`[Attempts Update] Setting attemptsRemaining to ${validatedTurnsRemaining} (max: ${maxForCharacter}, backend: ${data.turnsRemaining})`);
          
          // Use functional update to ensure we're using the latest state
          setAttemptsRemaining(prev => {
            console.log(`[Attempts Update] setAttemptsRemaining called - prev: ${prev}, new: ${validatedTurnsRemaining}`);
            return validatedTurnsRemaining;
          });
          
          // Verify the update
          setTimeout(() => {
            console.log(`[Attempts Update] After 100ms, checking if state updated correctly...`);
          }, 100);
        }
      } else {
        // Fallback: decrement attempts remaining if not in response
        console.log(`[Attempts Update] data.turnsRemaining is undefined, using fallback decrement`);
        setAttemptsRemaining(prev => {
          const newValue = Math.max(0, prev - 1);
          console.log(`[Attempts Update] Fallback: ${prev} -> ${newValue}`);
          return newValue;
        });
      }

      // Add small delay before showing Jamie's response for smoother transition
      setTimeout(() => {
        console.log('[Adding Message to State] jamieMessage:', {
          id: jamieMessage.id,
          personaStage: jamieMessage.personaStage,
          messagePreview: jamieMessage.message.substring(0, 50)
        });
        setMessages(prev => {
          const newMessages = [...prev, jamieMessage];
          console.log('[State Update] Total messages:', newMessages.length, 'Latest personaStage:', jamieMessage.personaStage);
          
          // CRITICAL: Save session AFTER messages are updated, with the correct attemptsRemaining
          if (userInfo && data.turnsRemaining !== undefined) {
            const maxForCharacter = getMaxAttempts(currentCharacter);
            const validatedTurnsRemaining = Math.min(data.turnsRemaining, maxForCharacter);
            
            const cleanMessages = newMessages.map(msg => ({
              id: msg.id,
              message: msg.message,
              isUser: msg.isUser,
              timestamp: msg.timestamp,
              isSessionEnd: msg.isSessionEnd,
              showFinalScore: msg.showFinalScore,
              dqScore: msg.dqScore,
              avgDqScore: msg.avgDqScore,
              sessionId: msg.sessionId,
              userId: msg.userId,
              personaStage: msg.personaStage || null
            }));
            
            const sessionData = {
              character: currentCharacter,
              messages: cleanMessages,
              attemptsRemaining: validatedTurnsRemaining,
              timestamp: new Date().toISOString()
            };
            
            localStorage.setItem(`session_${userInfo.id}_${currentCharacter}`, JSON.stringify(sessionData));
            console.log('[State Update] Saved session after message added, attemptsRemaining:', validatedTurnsRemaining);
          }
          
          return newMessages;
        });
        setConnectionStatus('connected');
        setIsLoading(false);
        setIsTyping(false);
        
        // Reset demo mode since backend is working
        if (demoMode) {
          debugLog('✅ Backend is working, resetting demo mode');
          setDemoMode(false);
        }
        
        // Scroll the AI response to the top of the view
        setTimeout(() => {
          const lastMessageElement = document.querySelector('[data-message-id="' + jamieMessage.id + '"]');
          if (lastMessageElement && chatContainerRef.current) {
            // Get the message's offset from the top of its container
            const messageOffsetTop = lastMessageElement.offsetTop;
            
            // Scroll the container so the message appears at the top
            chatContainerRef.current.scrollTo({
              top: messageOffsetTop,
              behavior: 'smooth'
            });
          }
        }, 200);
        
        // Clear thinking animation
        if (window.thinkingInterval) {
          clearInterval(window.thinkingInterval);
          window.thinkingInterval = null;
        }
        
        // Check if session should end after Jamie's response
        // Don't end if we're waiting for a final response to the closing message
        const isWaitingForFinalResponse = data.isWaitingForFinalResponse || false;
        // Use the backend's turnsRemaining directly (not the state variable which might be stale)
        const turnsRemainingFromBackend = data.turnsRemaining !== undefined 
          ? Math.min(data.turnsRemaining, getMaxAttempts(currentCharacter))
          : attemptsRemaining;
        console.log(`[Session End Check] isWaitingForFinalResponse=${isWaitingForFinalResponse}, turnsRemainingFromBackend=${turnsRemainingFromBackend}, attemptsRemaining state=${attemptsRemaining}`);
        if (!isWaitingForFinalResponse && turnsRemainingFromBackend <= 0) {
          setTimeout(() => {
            // Get the latest DQ scores
            const allMessages = [...messages, jamieMessage];
            const characterMessages = allMessages.filter(msg => !msg.isUser && msg.dqScore);
            const latestMessage = characterMessages.length > 0 ? characterMessages[characterMessages.length - 1] : null;
            
            // Try multiple sources for final DQ score
            let finalDqScore = latestMessage?.dqScore || data.dq_score;
            
            // If still undefined, try to get from jamieMessage directly
            if (!finalDqScore && jamieMessage.dqScore) {
              finalDqScore = jamieMessage.dqScore;
            }
            
            // If still undefined, try to get from any recent message with dqScore
            if (!finalDqScore) {
              const recentMessages = allMessages.filter(msg => !msg.isUser && msg.dqScore).slice(-3);
              if (recentMessages.length > 0) {
                finalDqScore = recentMessages[recentMessages.length - 1].dqScore;
              }
            }
            
            console.log('🔍 Session End - DQ Score Sources:', {
              latestMessageDqScore: latestMessage?.dqScore,
              dataDqScore: data.dq_score,
              jamieMessageDqScore: jamieMessage.dqScore,
              finalDqScore,
              characterMessagesCount: characterMessages.length
            });
            
            // Check if user achieved 0.7 or higher score
            const currentProgress = getJamieProgress();
            const hasWon = currentProgress >= 70; // 70% = 0.7 score
            
            // Different messages for assessment vs game mode
            const isAssessment = characterData[currentCharacter].gameMode === 'assessment';
            
            const personaName = characterData[currentCharacter].name;
            const sessionEndMessage = {
              id: Date.now() + 2,
              message: isAssessment
                ? hasWon
                  ? `Nicely done! You completed ${personaName}'s assessment with a strong performance. Here's a breakdown of your coaching across the six dimensions of decision quality:`
                  : `Assessment complete. You've finished ${personaName}'s coaching session. Here's a breakdown of your coaching across the six dimensions of decision quality:`
                : hasWon
                  ? `Nicely done! You reached ${currentProgress}% progress with ${personaName}. Here's a breakdown of your coaching across the six dimensions of decision quality:`
                  : `Session complete. You've used all ${getMaxAttempts(currentCharacter)} attempts with ${personaName}. Here's a breakdown of your coaching across the six dimensions of decision quality:`,
              isUser: false,
              timestamp: new Date().toISOString(),
              isSessionEnd: true,
              hasWon: hasWon,
              dqScore: finalDqScore,
              showFinalScore: true,
              personaName: personaName
            };
            
            // Update user progress
            if (userInfo) {
              // Calculate rawFinalScore from finalDqScore
              let rawFinalScore = 0;
              if (finalDqScore && typeof finalDqScore === 'object') {
                const dqValues = Object.values(finalDqScore)
                  .filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v) && v >= 0 && v <= 1);
                if (dqValues.length > 0) {
                  rawFinalScore = Math.min(...dqValues); // Minimum DQ score (weakest link)
                }
              } else if (typeof finalDqScore === 'number' && !isNaN(finalDqScore) && isFinite(finalDqScore)) {
                rawFinalScore = finalDqScore;
              }
              
              console.log('💾 Saving session progress:', {
                character: currentCharacter,
                rawFinalScore,
                finalDqScore,
                hasFinalDqScore: !!finalDqScore,
                finalDqScoreType: typeof finalDqScore,
                dqScoreValues: finalDqScore && typeof finalDqScore === 'object' ? Object.values(finalDqScore) : [finalDqScore],
                allMessagesWithDqScore: allMessages.filter(msg => !msg.isUser && msg.dqScore).map(msg => ({
                  id: msg.id,
                  hasDqScore: !!msg.dqScore,
                  dqScore: msg.dqScore
                }))
              });
              
              // If rawFinalScore is still 0, try to get from the last message's avgDqScore
              if (rawFinalScore === 0 && latestMessage?.avgDqScore) {
                console.log('⚠️ rawFinalScore is 0, trying avgDqScore from latestMessage:', latestMessage.avgDqScore);
                rawFinalScore = latestMessage.avgDqScore;
              }
              
              // Final fallback: if still 0, warn but don't save 0
              if (rawFinalScore === 0) {
                console.warn('⚠️ WARNING: rawFinalScore is 0! This will result in 0.00 score. finalDqScore:', finalDqScore);
              }
              const personaStage = characterData[currentCharacter].gameMode === 'assessment' ? null : (data?.persona_stage ?? null);
              const progressData = {
                finalScore: rawFinalScore,
                rawScore: rawFinalScore,
                stage: personaStage,
                attemptsUsed: getMaxAttempts(currentCharacter), // All attempts were used when session ends
                mode: characterData[currentCharacter].gameMode,
                dqScores: finalDqScore,
                completed: characterData[currentCharacter].gameMode === 'assessment' ? true : (rawFinalScore >= 0.7),
                messages: [...messages, sessionEndMessage] // Include the full chat transcript with end message
              };
              // Use the correct auth service based on USE_SUPABASE_AUTH flag
              // Await progress update and refresh userInfo to update tooltip with latest score
              (async () => {
                try {
                  if (USE_SUPABASE_AUTH) {
                    const result = await supabaseAuthService.updateProgress(currentCharacter, progressData);
                    console.log('💾 Progress update result:', result);
                    // Refresh userInfo after progress is saved
                    const updatedUser = supabaseAuthService.getCurrentUser();
                    if (updatedUser) {
                      const jamieProgress = updatedUser.progress?.jamie;
                      const lastSession = jamieProgress?.lastSession;
                      console.log('💾 Updated user progress:', {
                        hasJamieProgress: !!jamieProgress,
                        lastSessionScore: lastSession?.score,
                        lastSessionRawScore: lastSession?.rawScore,
                        sessionsCount: jamieProgress?.sessions?.length
                      });
                      setUserInfo(updatedUser);
                    }
                  } else {
                    await authService.updateProgress(currentCharacter, progressData);
                    // Refresh userInfo after progress is saved
                    const updatedUser = authService.getCurrentUser();
                    if (updatedUser) {
                      setUserInfo(updatedUser);
                    }
                  }
                } catch (error) {
                  console.error('💾 Error updating progress:', error);
                }
              })();
              
              // Clear in-progress session since it's complete
              clearInProgressSession(currentCharacter);
            }
            
            setMessages(prev => [...prev, sessionEndMessage]);
            if (finalDqScore) {
              requestSessionReview([...allMessages, sessionEndMessage], finalDqScore, personaName);
            }
          }, 1000);
        }
      }, 200);
    } catch (error) {
      console.error('Detailed error sending message:', error);
      setConnectionStatus('failed');
      
      // Backend failed - show error message instead of switching to demo mode
      debugLog('❌ Backend failed');
      
      // Show error message to user
      const errorMessage = {
        id: Date.now() + 1,
        message: error.message && error.message.includes('503') 
          ? "The backend service is temporarily unavailable. Please wait a moment and try again. The service should be back online shortly."
          : "Unable to connect to the backend service. Please check your connection and try again.",
        isUser: false,
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      // Add small delay before showing error message for smoother transition
      setTimeout(() => {
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        setIsTyping(false);
        
        // Clear thinking animation
        if (window.thinkingInterval) {
          clearInterval(window.thinkingInterval);
          window.thinkingInterval = null;
        }
      }, 200);
    }
  };

  // Handle message input change (optimized to prevent flashing)
  const handleMessageChange = (e) => {
    setCurrentMessage(e.target.value);
    adjustMessageInputHeight(e.target);
  };

  // Handle enter key (optimized)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Allow Shift+Enter for new lines
  };

  // Safety check: if userInfo exists but currentView is null, default to homepage
  // BUT: if we're still checking auth or restoring view, don't default yet (will show loading instead)
  const safeCurrentView = (userInfo && !currentView && !isAuthChecking) ? 'homepage' : currentView;
  
  // Additional safety: preserve chat view during progress loading
  // Always preserve chat view if user is in a chat session, regardless of loading state
  const finalCurrentView = (currentView === 'chat') ? 'chat' : safeCurrentView;

  // Determine if we should show the shared visual
  // Don't show during loading to prevent flash
  const isAuthOrViewLoading = isAuthChecking || (userInfo && !currentView);
  const showSharedVisual = !isAuthOrViewLoading && (!userInfo || finalCurrentView === 'homepage');

  // Debug logging for routing
  debugLog('App.js render - userInfo:', !!userInfo, 'currentView:', currentView, 'safeCurrentView:', safeCurrentView, 'finalCurrentView:', finalCurrentView, 'isLoadingProgress:', isLoadingProgress, 'isAuthOrViewLoading:', isAuthOrViewLoading);

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes collapseToBottomRight {
          from {
            opacity: 1;
            transform: translate(0, 0) scale(1);
          }
          to {
            opacity: 0;
            transform: translate(calc(50vw - 2rem), calc(50vh - 4.5rem)) scale(0.15);
          }
        }
      `}</style>
      {/* Shared visual that persists across login and homepage */}
      {/* Don't render during loading to prevent flash */}
      {showSharedVisual && <SharedVisual />}
      
      {/* Page Content */}
      {isAuthOrViewLoading ? (
        // Show loading while checking auth OR while view is being restored
        // This ensures we don't render homepage or landing page before we know the correct view
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : !userInfo ? (
        <LandingPage onLogin={handleLogin} onSignUp={handleLogin} />
      ) : currentView === 'homepage' ? (
        <>
          <HomePage 
            userInfo={userInfo}
            gameMode={gameMode}
            onStartCoaching={handleStartCoaching}
            onLogout={handleLogout}
            onSettings={handleSettings}
            onCharacterClick={handleCharacterClick}
            onAdminClick={() => setCurrentView('admin')}
            currentView={currentView}
            userProgress={(() => {
              const progress = userInfo?.progress;
              if (!progress) {
                console.warn('⚠️ WARNING: userInfo?.progress is undefined! userInfo:', userInfo);
              } else {
                console.log('✅ Passing userProgress to HomePage:', progress);
              }
              return progress;
            })()}
            isLoadingProgress={isLoadingProgress}
            onResetLoading={resetLoadingState}
          />
          
        </>
      ) : finalCurrentView === 'dashboard' ? (
        <UserDashboard 
          userInfo={userInfo}
          onBackToHome={() => setCurrentView('homepage')}
        />
      ) : finalCurrentView === 'admin' ? (
        <AdminDashboard 
          userInfo={userInfo}
          onBackToHome={() => setCurrentView('homepage')}
          onLogout={handleLogout}
          onSettings={() => setCurrentView('settings')}
          currentView={currentView}
        />
      ) : finalCurrentView === 'settings' ? (
        <SettingsPage 
          userInfo={userInfo}
          onBackToHome={() => setCurrentView('homepage')}
          onLogout={handleLogout}
          onAdminClick={() => setCurrentView('admin')}
          currentView={currentView}
        />
      ) : finalCurrentView === 'classrooms' ? (
        <TeacherClassrooms 
          onBackToHome={() => setCurrentView('homepage')}
          onViewClassroom={(classroomId) => {
            debugLog('App.js: onViewClassroom called with classroomId:', classroomId);
            setSelectedClassroomId(classroomId);
            setCurrentView('classroom-detail');
            debugLog('App.js: Set currentView to classroom-detail');
          }}
        />
      ) : finalCurrentView === 'classroom-detail' ? (
        (() => {
          debugLog('App.js: Rendering ClassroomDetail, classroomId:', selectedClassroomId);
          return (
            <ClassroomDetail 
              classroomId={selectedClassroomId}
              onBack={() => setCurrentView('classrooms')}
            />
          );
        })()
      ) : finalCurrentView === 'chat' ? (
        <div className="bg-white h-screen w-full flex flex-col sm:flex-row">
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .message-enter {
          animation: slideInUp 0.3s ease-out;
        }
        
        .progress-circle {
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .progress-arc {
          transition: stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .progress-text {
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        @keyframes progressPulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        
        .progress-animate {
          animation: progressPulse 0.8s ease-out;
        }
      `}</style>
      
      {/* Left Sidebar - Hidden on mobile */}
      <div className="hidden sm:flex w-[320px] flex-col" style={{ padding: '29px' }}>
        {/* Page Title */}
        <div className="mb-[100px]">
          <h1 className="text-[25px] font-bold text-black leading-[28px]">
            Decision<br />Coach
          </h1>
        </div>
        
        {/* Character Info */}
        <div className="mb-10">
          <h2 className="text-[20px] font-semibold text-[#363636] mb-0" style={{ fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 400 }}>{characterData[currentCharacter].name}</h2>
          <p className="text-[16px] text-[#363636]">{characterData[currentCharacter].title}</p>
        </div>
        
        {/* Context Section - Show only after first message in both modes */}
        {messages.length > 0 && (
          <div className="mb-6 max-w-[250px]">
            <h3 className="text-[14px] font-semibold text-[#363636] mb-1" style={{ fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 400 }}>
              Scenario
            </h3>
            <p className="text-[12px] text-[#535862] leading-relaxed">
              {characterData[currentCharacter].context}
            </p>
          </div>
        )}
        
        {/* Progress Bar - Only show in Game mode */}
        {characterData[currentCharacter].gameMode === 'game' && (
          <div className="mb-2 mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px] font-medium text-[#535862]">{characterData[currentCharacter].progressLabel}</span>
              <span className="text-[12px] font-semibold text-[#181d27]">{animatedProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-1000 ease-out ${isProgressAnimating ? 'progress-animate' : ''}`}
                style={{ width: `${animatedProgress}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {/* Character's State - Only show in Game mode */}
        {characterData[currentCharacter].gameMode === 'game' && (
          <p className="text-[16px] font-medium text-[#797979] text-left mb-4">
            {getCharacterState()}
          </p>
        )}
        
        
        {/* Spacer to push navigation to bottom */}
        <div className="flex-1"></div>
      </div>
      
      
      {/* Mobile Title - Fixed Position */}
      <div className="block sm:hidden fixed top-0 left-0 right-0 z-40 bg-white px-6 py-4">
        <div className="text-black font-bold text-[25px] leading-[28px]">
          <div>Decision</div>
          <div>Coach</div>
        </div>
      </div>

      {/* Mobile Hamburger Menu - Fixed Position */}
      <div className="fixed top-4 right-4 sm:hidden z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-lg border text-gray-600 hover:bg-gray-50 transition-colors"
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        
        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute top-12 right-0 bg-white rounded-lg shadow-lg border p-2 min-w-[200px]">
            <button 
              onClick={() => {
                setCurrentView('homepage');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-left text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>
            
            {userInfo?.role === 'teacher' && (
              <button 
                onClick={() => {
                  setCurrentView('admin');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded transition-colors ${
                  currentView === 'admin' 
                    ? 'text-blue-600 hover:bg-blue-50' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </button>
            )}
            
            <button 
              onClick={() => {
                handleSettings();
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-left text-gray-600 hover:bg-gray-50 rounded transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </button>
            
            <button 
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-left text-gray-600 hover:bg-gray-50 rounded transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Desktop Navigation Bar - positioned outside sidebar for proper click events */}
      <div className="hidden sm:block absolute bottom-6 left-6 z-50">
        <div className="bg-white rounded-lg shadow-lg p-4 flex items-center space-x-4 border w-fit">
          <button 
            onClick={() => setCurrentView('homepage')}
            className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
              currentView === 'homepage' 
                ? 'text-blue-600 hover:bg-blue-50' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Home className="w-5 h-5" />
          </button>
          {userInfo?.role === 'teacher' && (
            <>
              <div className="w-px h-6 bg-gray-300"></div>
              <button 
                onClick={() => setCurrentView('admin')}
                className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                  currentView === 'admin' 
                    ? 'text-blue-600 hover:bg-blue-50' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                title="Admin Dashboard"
              >
                <BarChart3 className="w-5 h-5" />
              </button>
            </>
          )}
          <div className="w-px h-6 bg-gray-300"></div>
          <button 
            onClick={handleSettings}
            className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
              currentView === 'settings' 
                ? 'text-blue-600 hover:bg-blue-50' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Settings className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-gray-300"></div>
          <button 
            onClick={handleLogout}
            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50 rounded transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile Character Info Button - Fixed Position Above Input Bar */}
      <div className="block sm:hidden fixed bottom-12 right-4 z-40">
        <button
          onClick={() => setShowCharacterInfo(true)}
          className="w-8 h-8 flex items-center justify-center bg-[#EFF8FF] rounded-full shadow-sm hover:bg-[#E0F2FE] transition-colors"
        >
          <User className="w-4 h-4 text-[#2C73EB]" />
        </button>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col relative w-full sm:w-auto">
        {/* Session Controls - Top Right - Hidden on Mobile */}
       <div className="hidden sm:flex absolute top-6 right-6 z-10 items-center gap-2">
         <button
           onClick={resetSession}
           className="w-7 h-7 sm:w-9 sm:h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
           title="Reset Session"
         >
           <RotateCcw className="w-3 h-3 sm:w-5 sm:h-5" />
         </button>
         <button
           onClick={() => {
             saveInProgressSession();
             setCurrentView('homepage');
           }}
           className="w-7 h-7 sm:w-9 sm:h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
           title="Exit Session"
         >
           <X className="w-3 h-3 sm:w-5 sm:h-5" />
         </button>
       </div>
        
        {/* Chat Messages Container */}
        <div ref={chatContainerRef} className="flex-1 bg-white sm:bg-[rgba(217,217,217,0.19)] overflow-y-auto relative pb-32 sm:pb-40">
          <div className="max-w-[866px] mx-auto px-3 py-6 sm:px-0 sm:py-[76px] flex flex-col gap-4 sm:gap-[46px] pt-24 sm:pt-20">
            {messages.length === 0 && (
              <div className="text-center py-8 sm:py-16">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#2C73EB] flex items-end justify-center mx-auto mb-4 sm:mb-6 shadow-lg overflow-hidden">
                    <img 
                      src={getAvatarImage(
                        currentCharacter,
                        (currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') 
                          ? getCharacterState()?.toLowerCase() || 'overwhelmed'
                          : null,
                        null, // No message object for empty state
                        null // No message index for empty state
                      )} 
                      alt={characterData[currentCharacter].name} 
                      className={`w-16 h-16 sm:w-20 sm:h-20 object-cover object-bottom`}
                      style={(currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') ? { opacity: 0.9 } : {}}
                    />
                  </div>
                <div className="absolute top-6 left-6 sm:top-8 sm:left-8 z-10">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 400 }}>
                    Start coaching {characterData[currentCharacter].name} {demoMode && <span className="text-xs sm:text-sm font-medium text-blue-600 bg-blue-100 px-2 sm:px-3 py-1 rounded-full ml-2">Demo Mode</span>}
                  </h2>
                </div>
                <p className="text-gray-600 max-w-lg mx-auto mb-6 sm:mb-8 text-base sm:text-lg leading-relaxed px-4">
                  {characterData[currentCharacter].context}
                </p>
                
                {!demoMode && connectionStatus === 'failed' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 max-w-md mx-auto mb-4 mx-4">
                    <p className="text-yellow-800 text-xs sm:text-sm mb-3">
                      ⚠️ Backend is temporarily unavailable. Switching to demo mode...
                    </p>
                    <div className="flex justify-center">
                      <button
                        onClick={() => setDemoMode(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                      >
                        Continue with Demo Mode
                      </button>
                    </div>
                  </div>
                )}
                
                {demoMode && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 max-w-md mx-auto mb-4 mx-4">
                    <p className="text-blue-800 text-xs sm:text-sm mb-3">
                      🎭 Demo mode active - Jamie will respond with simulated conversations and DQ scores.
                    </p>
                    <button
                      onClick={() => {
                        setDemoMode(false);
                        testConnection();
                      }}
                      className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm"
                    >
                      Try Real Backend
                    </button>
                  </div>
                )}
              </div>
            )}

            {messages.map((msg, index) => {
              // Debug: Log message details for Andres and Kavya
              if ((currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex') && !msg.isUser) {
                const effectiveStage = getEffectiveStage(index, messages);
                if (!msg.personaStage || !('personaStage' in msg)) {
                  console.warn(`[Message ${index}] ⚠️ MISSING personaStage!`, {
                    id: msg.id,
                    isUser: msg.isUser,
                    hasPersonaStage: 'personaStage' in msg,
                    personaStage: msg.personaStage,
                    effectiveStage: effectiveStage,
                    allKeys: Object.keys(msg),
                    fullMessage: msg
                  });
                } else {
                  console.log(`[Message ${index}] ✅ personaStage:`, msg.personaStage, 'effectiveStage:', effectiveStage, 'msg.id:', msg.id);
                }
              }
              return (
                <React.Fragment key={msg.id}>
                {msg.isUser ? (
                  // User messages - blue bubble
                  <div className="flex justify-end message-enter mb-4 sm:mb-0" data-message-id={msg.id}>
                    <div className="bg-[#e8f1f8] rounded-[5px] shadow-[0px_6px_20px_10px_rgba(200,201,201,0.11)] px-4 py-4 sm:px-6 sm:py-6 max-w-[85%] sm:max-w-[605px] ml-8 mr-1 sm:mx-0">
                      <p className="text-sm sm:text-[16px] text-[#363636] leading-[22px] sm:leading-[26px]">
                        {msg.message}
                      </p>
                    </div>
                  </div>
                ) : (
                  // Jamie's messages - white bubble with avatar and DQ scores
                  <div className="flex gap-1 sm:gap-[30px] items-start message-enter mb-4 sm:mb-0" data-message-id={msg.id}>
                  {/* Character Avatar */}
                  <div className="w-8 h-8 sm:w-[70px] sm:h-[70px] rounded-full bg-[#2C73EB] flex items-end justify-center flex-shrink-0 overflow-hidden">
                    <img 
                      src={getAvatarImage(
                        currentCharacter, 
                        (() => {
                          // For Andres/Kavya: Latest message uses current progress-based stage, older messages use their personaStage
                          if (currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') {
                            // Find the index of the last Andres/Kavya message
                            const andresMessages = messages.filter(m => !m.isUser);
                            const isLatestMessage = andresMessages.length > 0 && andresMessages[andresMessages.length - 1].id === msg.id;
                            
                            if (isLatestMessage) {
                              // Latest message: Use current progress-based stage to match progress bar
                              const currentStage = getCharacterState();
                              return currentStage ? currentStage.toLowerCase() : 'overwhelmed';
                            } else {
                              // Older messages: Use their personaStage if available, otherwise default to overwhelmed
                              return msg.personaStage || 'overwhelmed';
                            }
                          }
                          // For other characters, use personaStage
                          return msg.personaStage || null;
                        })(),
                        msg, // Pass message object
                        index // Pass message index
                      )} 
                      alt={characterData[currentCharacter].name} 
                      className={`w-8 h-8 sm:w-[70px] sm:h-[70px] object-cover object-bottom`}
                      style={(currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') ? { opacity: 0.9 } : {}}
                    />
                  </div>
                    
                    {/* Jamie's Message */}
                    <div className="bg-white rounded-[5px] shadow-[0px_6px_20px_10px_rgba(200,201,201,0.11)] px-4 py-4 sm:px-[33px] sm:py-6 max-w-[597px] ml-1 mr-8 sm:mx-0">
                      <div className="flex flex-col gap-[25px]">
                        {/* Message Text */}
                        <div className="text-sm sm:text-[16px] text-[#333333] leading-[22px] sm:leading-[26px]">
                          <p className="whitespace-pre-wrap">{msg.message}</p>
                          
                          {/* Session summary report (shown at end of every session) */}
                          {msg.isSessionEnd && msg.dqScore && (
                            <SessionSummaryReport
                              dqScore={msg.dqScore}
                              personaName={msg.personaName || characterData[currentCharacter].name}
                              hasWon={msg.hasWon}
                              onStartNew={resetSession}
                              review={sessionReview}
                              reviewStatus={sessionReviewStatus}
                            />
                          )}

                          {/* Fallback: session ended without a scored final turn */}
                          {msg.isSessionEnd && !msg.dqScore && (
                            <div className="mt-6 flex justify-center">
                              <button
                                onClick={resetSession}
                                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                                  msg.hasWon
                                    ? "bg-green-500 text-white hover:bg-green-600"
                                    : "bg-[#2C73EB] text-white hover:bg-[#1e5bb8]"
                                }`}
                              >
                                Start New Session
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Gray Hairline Divider */}
                        {msg.dqScore && characterData[currentCharacter].gameMode === 'game' && !msg.isSessionEnd && (
                          <div className="w-full h-px bg-gray-300"></div>
                        )}
                        
                        {/* DQ Score Panel */}
                        {msg.dqScore && characterData[currentCharacter].gameMode === 'game' && !msg.isSessionEnd && (
                          <div className="flex flex-col gap-[25px]">
                            {/* DQ Header */}
                            <div>
                              <p className="text-[16px] font-semibold text-[#797979] leading-[26px]">
                                Decision Quality Score: {(msg.avgDqScore !== undefined ? msg.avgDqScore : safeMinDqScore(msg.dqScore)).toFixed(1)}/1.0
                              </p>
                            </div>
                            
                            {/* DQ Metrics Grid */}
                            <div className="grid grid-cols-2 gap-[34px]">
                              {[
                                { key: 'framing', label: 'Framing', color: 'bg-[#0385c3]', value: msg.dqScore.framing },
                                { key: 'alternatives', label: 'Alternatives', color: 'bg-[#53b723]', value: msg.dqScore.alternatives },
                                { key: 'information', label: 'Information', color: 'bg-[#ff8210]', value: msg.dqScore.information },
                                { key: 'values', label: 'Values', color: 'bg-[#991bb2]', value: msg.dqScore.values },
                                { key: 'reasoning', label: 'Reasoning', color: 'bg-[#d01102]', value: msg.dqScore.reasoning },
                                { key: 'commitment', label: 'Commitment', color: 'bg-[#ffb20d]', value: msg.dqScore.commitment }
                              ].map(metric => (
                                <div key={metric.key} className="flex flex-col gap-[6px]">
                                  <div className="flex justify-between items-center">
                                    <p className="text-[14px] text-[#797979]">{metric.label}</p>
                                    <p className="text-[14px] text-[#797979] text-right">{metric.value.toFixed(1)}</p>
                                  </div>
                                  <div className="relative w-full h-[11px] bg-[rgba(217,217,217,0.44)] rounded-[10px]">
                                    <div 
                                      className={`absolute top-0 left-0 h-full rounded-[10px] ${metric.color}`}
                                      style={{ width: `${metric.value * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                </React.Fragment>
              );
            })}
            
              {isTyping && (
                <div className="flex gap-1 sm:gap-[30px] items-start mb-4 sm:mb-0">
                  <div className="w-8 h-8 sm:w-[70px] sm:h-[70px] rounded-full bg-[#2C73EB] flex items-end justify-center flex-shrink-0 overflow-hidden">
                    <img 
                      src={getAvatarImage(
                        currentCharacter, 
                        // For typing indicator (current/latest state), use progress-based stage to match progress bar
                        (currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah')
                          ? getCharacterState()?.toLowerCase() || 'overwhelmed'
                          : (messages.filter(m => !m.isUser && m.personaStage).length > 0 
                              ? messages.filter(m => !m.isUser && m.personaStage).slice(-1)[0].personaStage 
                              : null),
                        null,
                        null // No message index for typing indicator
                      )} 
                      alt={characterData[currentCharacter].name} 
                      className={`w-8 h-8 sm:w-[70px] sm:h-[70px] object-cover object-bottom`}
                      style={(currentCharacter === 'andres' || currentCharacter === 'kavya' || currentCharacter === 'alex' || currentCharacter === 'sarah') ? { opacity: 0.9 } : {}}
                    />
                  </div>
                  <div className="bg-white rounded-[5px] shadow-[0px_6px_20px_10px_rgba(200,201,201,0.11)] px-4 py-4 sm:px-[33px] sm:py-6 ml-1 mr-8 sm:mx-0">
                    <div className="flex items-center">
                      <span 
                        className="text-sm sm:text-[16px] text-[#6B7280] font-medium transition-all duration-200 ease-in-out"
                        style={{
                          opacity: isTextTransitioning ? 0.3 : 1,
                          transform: isTextTransitioning ? 'translateY(2px)' : 'translateY(0px)'
                        }}
                      >
                        {thinkingText}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Fixed Input Bar at Bottom */}
          <div className="fixed bottom-0 left-0 right-0 p-2 sm:p-6 z-10">
            <div className="max-w-[866px] mx-auto flex flex-col items-center sm:mr-32 relative">
              {/* Attempts Remaining - Above input, positioned above blue input bar */}
              <div className="mb-0 sm:mb-0 self-start ml-3 sm:ml-36 flex-shrink-0">
                <div className="bg-[#eff8ff] rounded-[16px] px-2 sm:px-3 py-1 w-fit">
                  <p className="text-xs sm:text-[14px] font-medium text-[#2c73eb] text-center">
                    {attemptsRemaining <= 0 ? "Session ended" : `${attemptsRemaining} attempts remaining`}
                  </p>
                </div>
              </div>
              
              {/* Blue Input Chat */}
                <div className="bg-[#538ff6] rounded-[25px] sm:rounded-[45px] w-full max-w-[400px] sm:max-w-[626px] px-3 sm:px-[29px] py-2 sm:py-[15px] flex items-center justify-between shadow-2xl">
                <textarea
                  ref={messageInputRef}
                  value={currentMessage}
                  onChange={handleMessageChange}
                  onKeyPress={handleKeyPress}
                  placeholder={attemptsRemaining <= 0 ? "Session ended - Start new session" : "Start coaching"}
                  className="bg-transparent text-white placeholder-white/80 flex-1 outline-none text-xs sm:text-[14px] font-semibold resize-none min-h-[20px] max-h-[120px] overflow-y-auto"
                  disabled={isLoading || attemptsRemaining <= 0}
                  rows={1}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !currentMessage.trim() || attemptsRemaining <= 0}
                  className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center disabled:opacity-50"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Character Info Popup */}
      {showCharacterInfo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 sm:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">{characterData[currentCharacter].name}</h2>
              <button
                onClick={() => setShowCharacterInfo(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 text-sm">{characterData[currentCharacter].title}</p>
            </div>
            
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Scenario</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {characterData[currentCharacter].context}
              </p>
            </div>
            
            {characterData[currentCharacter].gameMode === 'game' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{characterData[currentCharacter].progressLabel}</span>
                  <span className="text-sm font-semibold text-gray-800">{animatedProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div 
                    className={`h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-1000 ease-out ${isProgressAnimating ? 'progress-animate' : ''}`}
                    style={{ width: `${animatedProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            {characterData[currentCharacter].gameMode === 'game' && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {getCharacterState()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Scenario Modal - shown after chat view loads when clicking on a character (MOBILE ONLY) */}
      {showScenarioModal && pendingCharacterId && isMobile && (
        <div 
          className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100] transition-opacity duration-300 ${
            isModalClosing ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ animation: isModalClosing ? 'none' : 'fadeIn 0.3s ease-out' }}
        >
          <div 
            className={`bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100 transition-all duration-300 ${
              isModalClosing 
                ? 'scale-95 opacity-0 translate-y-4 sm:scale-95 sm:opacity-0 sm:translate-y-4' 
                : 'scale-100 opacity-100 translate-y-0'
            }`}
            style={{ 
              animation: isModalClosing 
                ? (isMobile 
                    ? 'collapseToBottomRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards' 
                    : 'none')
                : 'slideDown 0.3s ease-out'
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{characterData[pendingCharacterId].name}</h2>
                <p className="text-gray-600 text-sm mt-1">{characterData[pendingCharacterId].title}</p>
              </div>
              <button
                onClick={handleStartChat}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Scenario</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {characterData[pendingCharacterId].context}
              </p>
            </div>
            
            {characterData[pendingCharacterId].gameMode === 'game' && (() => {
              const progress = getCharacterProgressForModal(pendingCharacterId);
              const state = getCharacterStateForModal(pendingCharacterId, progress);
              
              return (
                <>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">{characterData[pendingCharacterId].progressLabel}</span>
                      <span className="text-sm font-semibold text-gray-800">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      {state}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
        </div>
      ) : (
        <div className="bg-white h-screen w-full flex items-center justify-center">
          <p className="text-gray-500">Unknown view: {finalCurrentView}</p>
        </div>
      )}
    </div>
  );
};

const JamieAI = () => {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  if (pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }

  if (pathname === '/about') {
    return <AboutPage />;
  }

  return <MainApp />;
};

export default JamieAI;
